/**
 * trendData.ts — Trend Reports data registry
 *
 * DATA CONTRACT:
 *   To replace with API data, swap the `trendData` constant with a
 *   fetching hook that returns the same `Record<string, TrendEntry>` shape.
 *
 *   The `trendCarouselItems` export is consumed by `useTrendCarouselItems`
 *   (same TrendCarouselItem shape — no hook signature change needed on switch).
 *
 * TO ADD A NEW COLLECTION:
 *   1. Add images to `public/imgs/trend/`
 *   2. Add a new entry to `trendData` with the collection's slug as key
 *   3. Done — routing, pages and carousel all pick it up automatically
 */

import type { ContentLinkProps } from "components/content-link";
import type { TrendCarouselItem } from "../start/trendCarousel.types";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MaterialSample {
  image?: string;
  colorHex: string;
  name: string;
  ralColor: string;
  material: string;
  shape: string;
  appearance: string;
  /** When present, "Check it now" links to /search with this productId filter */
  productId?: string;
  /** Full relative search URL (e.g. "/search?preliminaryColorCode=...&colorSystem=...") */
  searchUrl?: string;
  /** CX-2764 link config — "View in Imagio" or overridden CTA behaviour. */
  link?: ContentLinkProps;
}

export interface TrendCardSubsection {
  title: string;
  text: string;
}

export interface MaterialGroup {
  image: string;
  samples: MaterialSample[];
}

export interface TrendCardDetail {
  title: string;
  collection: string;
  collectionSlug: string;
  heroImage: string;
  /** Body copy for the "About this theme" section below the hero */
  description: string;
  /** Short italicized quote shown in the hero, above the attribution line */
  heroQuote?: string;
  /** Attribution byline shown under the hero quote (e.g. "— Name, Title") */
  heroAttribution?: string;
  /** Color swatches shown in the hero, in place of text tag pills */
  heroSwatches?: TrendColor[];
  /** CMF material description shown below the hero */
  cmfDescription?: string;
  /** Two-column subsections shown between CMF description and material samples */
  subsections?: TrendCardSubsection[];
  tags: string[];
  badge?: string;
  /** Shared product render image for the material samples card (single group) */
  productRender?: string;
  /** Multiple grouped cards, each with its own chip image + material rows */
  materialGroups?: MaterialGroup[];
  materialSamples: MaterialSample[];
}

export interface TrendColor {
  hex: string;
  name: string;
}

export interface TrendCardItem {
  slug: string;
  title: string;
  image: string;
  description?: string;
  /** Colors shown as swatch + label chips in card hover content */
  colors?: TrendColor[];
}

export interface TrendSection {
  title: string;
  /** Small label shown next to the section title (e.g. "Inspired by nature"). */
  subtitle?: string;
  cards: TrendCardItem[];
  /** Per-section theme pills; falls back to TrendEntry.themes when absent. */
  themes?: string[];
  /** Per-section intro paragraphs; falls back to TrendEntry.introText when absent. */
  introText?: string[];
  /** Per-section quote; falls back to TrendEntry.quote when absent. */
  quote?: string;
  /** Category hero image for the carousel panel background. */
  heroImage?: string;
}

export interface TrendEntry {
  slug: string;
  title: string;
  heroImage: string;
  /** Short description shown on the hero overlay; falls back to introText[0] */
  heroDescription?: string;
  /** Pill label shown above the hero title — defaults to "Design Trend Report" */
  heroChip?: string;
  /** Label for the hero's scroll-down CTA — defaults to "Explore Collection" */
  heroExploreLabel?: string;
  /** Heading shown above the intro body text (e.g. "Unique Collaboration") */
  introTitle?: string;
  themes: string[];
  introText: string[];
  quote: string;
  sections: TrendSection[];
  /** Floating tag label on hero — defaults to "CMF Collection" */
  tagLabel?: string;
  /** Keyed by cardSlug */
  cardDetails: Record<string, TrendCardDetail>;
}

const imagioLink = (searchPath: string): ContentLinkProps => ({
  variant: "view-in-imagio",
  imagioUrl: searchPath,
});

// ── Dataset ───────────────────────────────────────────────────────────────────

export const trendData: Record<string, TrendEntry> = {
  // ── CMF Design Trends 2025+ Edition (CX-2779) ─────────────────────────────
  "cmf-design-trends-2025": {
    slug: "cmf-design-trends-2025",
    title: "CMF Design Trends 2025+ Edition",
    heroImage: "/imgs/trend/cmf2025-hero.jpg",
    tagLabel: "CMF 2025",
    heroDescription:
      "Explore the latest Color, Material & Finish directions across Electronics, Healthcare, and Automotive — translating emerging cultural signals into actionable CMF strategies.",
    themes: ["WANDERLUST", "MOBILITY", "FREEDOM"],
    introText: [
      "No longer the simple assembly-line produced metal boxes of yesteryear; today's automobiles are sophisticated machines that have surpassed their original purpose of transporting passengers from one destination to another. Vehicles are not only designed for meaningful integration of space and functionality but also viewed as a means of solving everyday problems and alleviating anxiety. As it becomes easier to mimic current designs, finding unique sources of inspiration can prove difficult — as such, many are looking to various cultural origins to gain fresh insights and perspectives.",
    ],
    quote:
      "«Within the symbols, patterns, and other hallmarks of early civilizations, it may be possible to uncover new and innovative blueprints for future mobility.»",
    sections: [
      {
        title: "Automotive",
        heroImage: "/imgs/trend/cmf2025-automotive.jpg",
        themes: ["WANDERLUST", "MOBILITY", "FREEDOM"],
        introText: [
          "No longer the simple assembly-line produced metal boxes of yesteryear; today's automobiles are sophisticated machines that have surpassed their original purpose of transporting passengers from one destination to another. Vehicles are not only designed for meaningful integration of space and functionality but also viewed as a means of solving everyday problems and alleviating anxiety. As it becomes easier to mimic current designs, finding unique sources of inspiration can prove difficult — as such, many are looking to various cultural origins to gain fresh insights and perspectives.",
        ],
        quote:
          "«Within the symbols, patterns, and other hallmarks of early civilizations, it may be possible to uncover new and innovative blueprints for future mobility.»",
        cards: [
          {
            slug: "ultra-vision",
            title: "Ultra Vision",
            image: "/imgs/trend/cmf2025-ultra-vision.jpg",
            description:
              "As digital technology makes increasing headway into smart mobility, autonomous driving technology and intelligent in-car assistants gradually become more widespread.\n\nTechnological development has always prioritized safety. When control is relinquished over the steering wheel and pedals, high-level automation shifts responsibility from the user to the system. The technological question now is how to navigate moral dilemmas where a choice must be made between the two and how to build trust in these vehicles.\n\nVehicles employ visual systems and radar to collect safety data and in-car assistants to communicate with people, who need an audible, visible, interactive co-pilot to share the driving experience with. The panoramic view on the front screen provides the means to take in the real driving environment from multiple perspectives. The information stream can likewise be hidden at a touch when no longer needed, and when the podcast selected by the in-car assistant finishes, the cover lifts to reveal that the journey has reached its destination.\n\nIn any case, safety technology will play a vital role in the future of automation, and emotional connections will raise the concept of human-machine symbiosis to new heights.",
            colors: [
              { hex: "#152838", name: "Sparkling Intelligence" },
              { hex: "#2E3031", name: "Black Crystal" },
            ],
          },
          {
            slug: "moving-castle",
            title: "Moving Castle",
            image: "/imgs/trend/cmf2025-moving-castle.jpg",
            description:
              'With the rise of remote work, more people are choosing to live as digital nomads who find "living in car" offering something a fixed, detached home cannot.\n\nInterior space is taking on the attributes of a real home, liberating our traditional understanding of what makes a home. Increasingly, these tiny mobile homes provide a space for expressing individual personality coupled with the immense freedom of mobility. Transform the interior of your vehicle into a personal home theater, launch yourselves into the depths of space as you project the starry sky onto your ceiling, and recharge and release stress anytime, anywhere. Bring solar panels and camping equipment and take it slow and natural, or join forces with like-minded individuals to form a co-living, exploring creative community writing its own story of liberation. RVs have grown from just another vehicle to embody an artistic lifestyle, where the cozy life on wheels finds meaning in being on the road.\n\nAs the demand for larger RVs continues to increase, brands will also be called upon to fulfill their commitment to the circular economy, adopt more environmentally friendly methods, and use recycled materials and eco-friendly products, empowering people to drive vehicles sourced from green materials towards a greener future.',
            colors: [
              { hex: "#8B7660", name: "Grit" },
              { hex: "#93D1CE", name: "Horizon Line" },
            ],
          },
          {
            slug: "power-capsule",
            title: "Power Capsule",
            image: "/imgs/trend/cmf2025-power-capsule.jpg",
            description:
              "Concerns about pollution from traditional energy vehicles and rising fuel costs are increasingly turning consumers to more environmentally friendly and economical modes of transportation.\n\nA survey singled out a lack of charging infrastructure as a major obstacle to promoting new energy transport. A more secure, persuasive energy storage solution is required before the era of new energy vehicles can truly take off.\n\nAs energy storage technology moves from industry to the home, a diversity of new solutions are coming to the attention of the automotive industry as well. The energy battery revolution is driving a shift towards environmentally friendly modes of travel with greater economy.",
            colors: [
              { hex: "#9D9D9D", name: "Levitation" },
              { hex: "#EE7541", name: "Gravitation Orange" },
            ],
          },
          {
            slug: "hyperlink",
            title: "Hyperlink",
            image: "/imgs/trend/cmf2025-hyperlink.jpg",
            description:
              "Multimodal transportation networks have given us greater autonomy in travel. Therefore, an increasing number of individuals are turning to personalized modes of travel.\n\nGone are the days of limited single-mode public transport. People may now drive or take the subway and then hop on an electric scooter, hoverboard, or electric wheelchair to reach their destinations. Micro-mobility enhances travel efficiency and is more convenient, flexible, and environmentally friendly.\n\nIntegrating shared vehicles into the transportation system will lead to better access to information and optimize transfers between vehicles during the journey. Micro-mobility products have filled the innovation gap for short-distance travel, underserved by public transportation. This also places higher demands on the light weight and foldability of micro-mobility vehicles. Household vehicles need to provide storage space for frequently used vehicles, and even vehicle components can serve as lightweight carriers. As inclusivity spreads to travel, from gender stereotypes to disabilities, designs in the micro-mobility sector will see more inclusive innovations. And finally, urban vertical space itself shows unlimited potential, as low-altitude aircraft break the constraints of concrete jungles and raise transportation services to unprecedented heights. Whether it's Generation Z riding the subway with a skateboard or elderly individuals using electric wheelchairs on buses, the possibilities for travel are boundless.",
            colors: [
              { hex: "#4D4E50", name: "Dark Matter" },
              { hex: "#BED5AE", name: "Bright Lime" },
            ],
          },
        ],
      },
      {
        title: "Electronics",
        heroImage: "/imgs/trend/cmf2025-electronics.jpg",
        themes: ["ASPIRATION", "INNOVATION", "CONNECTIVITY"],
        introText: [
          "The electronics industry is undergoing a transformation driven by artificial intelligence, digital exploration, and a renewed sense of global community. As boundaries between physical and digital blur, designers seek materials that embody both technological precision and human warmth. These four trend directions capture the spirit of aspiration — reaching beyond current limitations toward a future where technology serves deeper human needs.",
        ],
        quote:
          "«Discover how actual and longer lasting key themes for the industry can be translated into a convincing CMF strategy and a makeable design.»",
        cards: [
          {
            slug: "ai-squad",
            title: "AI Squad",
            image:
              "https://mediahub.covestro.com/transform/abb598ae-b57e-4bae-a359-dfcbaf6394d2/AI-Squad",
            description:
              'Large language models and generative artificial intelligence have exploded onto the scene, thrusting this new technology into our work and everyday lives. As it moves from the laboratory into daily life, a deeper connection between humans and machines must be built if we are to forge greater understanding, trust, and cooperation. When asked what role AI plays at work and in the home, GPT-4 replied that it is our "partner."\n\nIn the workplace, AI has been charged with repetitive tasks like gathering information and organizing data but can also assist in creative tasks like copywriting and image editing, freeing people up to focus on higher value analytical work. Service industry AI can handle mechanical customer service processes even to the point of human interaction requiring emotional intelligence. This efficient working partner, developed through machine learning, will revolutionize the productivity landscape and help organizations address various challenges.\n\nAI in the home is taking on the role of a home assistant. According to a recent poll, 75% of respondents say "please" and "thank you" to their AI assistants, indicative of a desire to establish a more intimate emotional bond with AI. Proactive AI assistants can integrate IoT information flows, provide customized recommendations, and interact with humans through virtual characters or subtle natural language processing. Communication covering all age groups makes the identity of "companion" more diverse: For Generation Alpha, AI-powered education covers classroom learning, smart toys, and interest development; for the elderly, AI provides health monitoring, and support for health and emotional care.',
            colors: [
              { hex: "#F2F4F1", name: "Organdie" },
              { hex: "#F2F3F3", name: "Feather" },
            ],
          },
          {
            slug: "digital-wandering",
            title: "Digital Wandering",
            image:
              "https://images.unsplash.com/photo-1664776788662-b4ef5d289103?q=80&w=1287&auto=format&fit=crop",
            description:
              "Modern industrial development has shifted the working environments of many from the confines of traditional office space to anywhere with an internet connection. AI technology has taken the new liberty even further, assuming repetitive tasks so that we are free to focus on creativity. The result is an increasing number of individuals choosing to freelance and self-publish and a new definition of working space within a virtual world requiring only that all-important internet connection.\n\nIn addition to changing the way we work, digital technology has also changed the way we think about work. Modern people, who prioritize personal growth over material possessions, have shifted their focus from work to self-fulfillment. Money is still necessary, but there is more of a feeling that sitting in a cubicle to earn a stable salary will not bring spiritual fulfillment or allow one to realize one's full potential.\n\nWith technology, we can now navigate freely between real and virtual worlds, overturning established concepts of space and pushing the boundaries of creativity: meeting with colleagues without ever leaving the comfort of your own home, free to travel to picturesque locations to create, and sharing ideas with the entire community at the touch of a finger.\n\nThe hybrid office has transformed the meaning of productivity and facilitated the integration of diverse living spaces. Immersive technology has gradually been embedded into various office products to provide computing power during work and a comfortable experience in daily life. Multi-functional laptops equipped with better webcams, microphones, and 3D displays replace limitations with a more immersive working experience. Slash workers (people who juggle multiple roles) can multitask more deftly to achieve that delicate work-life balance.\n\nA move towards flexibility in work has led this new generation of office worker to embrace lighter, more scalable portable devices with extended battery life more suited to the agile office of the digital nomad.",
            colors: [
              { hex: "#791937", name: "Berry" },
              { hex: "#8B2224", name: "Pomegranate" },
            ],
          },
          {
            slug: "boundless-community",
            title: "Boundless Community",
            image:
              "https://images.unsplash.com/photo-1712924653565-1cd256dcd2d1?q=80&w=1615&auto=format&fit=crop",
            description:
              'It is common for millennials to live alone in big cities, spend lots of time commuting, and bear the burden of high rents, making co-living in central urban areas worldwide increasingly attractive. Residents get their own private rooms but share facilities like kitchens and bathrooms.\n\nCo-living blurs the boundaries between friend and family, promoting a hybrid family-style model and repositioning housing as a service. It may, in fact, be the ideal lifestyle for young people to have a loved one close at hand, a friend next door, working and living together. Millennials are increasingly embracing cohabitation and capable of filling the need for intimacy with shallow social relationships. Co-living can be a source of freedom and flexibility, though how to balance "private" with the "communal" may place inclusivity on a collision course with the future design of co-living spaces and furnishings.\n\nUnder one roof we live together but seek to retain our independence. The ability to preserve one\'s privacy within limited space is an essential pre-condition to co-living: shared products must be self-cleaning, easy to assemble, convenient to maintain, and able to protect user information and data privacy. As households take on more inhabitants, a personalized experience can lend that human touch we seek. Shared products can remember user preferences and provide services with the requisite emotional value. A purchase from a vending machine may remind you of warm greetings from the owner of the grocery store downstairs.\n\nDiverse public spaces provide residents with opportunities to interact, inspire one another, and attain a higher quality of life with like-minded individuals. By enabling shared living, the "boundless" public spaces have allowed nomadic individuals not only to meet their social needs but also to potentially provide each other with spiritual support.',
            colors: [
              { hex: "#AFC2C3", name: "Foggy Blue" },
              { hex: "#CDCAC4", name: "Smoky Gray" },
            ],
          },
          {
            slug: "tech-nature",
            title: "Tech × Nature",
            image:
              "https://images.unsplash.com/photo-1593586710811-c92ee75ddf6d?q=80&w=3174&auto=format&fit=crop",
            description:
              "With increasing environmental awareness, the pursuit of sustainable living is not just a trend but has become our habit. Cherishing natural resources and protecting nature's gifts have become priorities. More and more individuals today are rethinking their life priorities and proactively changing their mindset to think is there a new way to combine technology and nature. An increasing number of individuals today are embracing a minimalist lifestyle and are starting to realize that technology and nature are not necessarily opposing ends. Is it possible for the two to work together in a sustainable way?\n\nInnovative spatial ecosystems can bring net-positive energy within reach, making our households more self-sufficient and independent of the national power grid. Stand-alone energy cells can be integrated seamlessly and organically into daily life, combining the aesthetic and the functional. Whether bold or understated, they can imbue our homes with aesthetic enjoyment and responsible, eco-friendly living.",
            colors: [
              { hex: "#657B5D", name: "Oasis" },
              { hex: "#C0B76A", name: "Wheat Field" },
            ],
          },
        ],
      },
      {
        title: "Healthcare",
        heroImage: "/imgs/trend/cmf2025-healthcare.jpg",
        themes: ["HYPERSPACE", "WELLBEING", "TRUST"],
        introText: [
          "Healthcare design is entering a new dimension — one where comfort, hygiene, and advanced technology converge. The Hyperspace direction envisions medical environments and devices that feel less clinical and more human. From cozy textures that reduce anxiety to transmittable health data rendered in calming palettes, these trends explore how material design can actively contribute to patient wellbeing and trust in medical innovation.",
        ],
        quote:
          "«Material choices in healthcare are no longer purely functional — they communicate care, innovation, and the promise of better outcomes.»",
        cards: [
          {
            slug: "cozy-groove",
            title: "Cozy Groove",
            image:
              "https://images.pexels.com/photos/7241294/pexels-photo-7241294.jpeg?auto=compress&cs=tinysrgb&w=1260",
            description:
              "We live in a society obsessed with efficiency, with bodies so busy there's hardly a second left for the soul. The result of this new life is a numbness that leaves us unable to empathize with others, incapable of self-awareness, overloaded emotionally, and increasingly isolated. And finally, freedom and tolerance have become new themes of thought, prompting people to reconsider alternative lifestyles beyond mainstream values.\n\nThe new generation, represented by Generation Z, tends to follow their inner preferences and freely express their personal style in life: whether it's adding their own touch to home decor through handmade projects, or finding relaxation and comfort amidst hand-planted greenery. Their focus on the personal life exceeds that of previous generations, as does their pursuit of value for money. Young people are embracing practical products to satisfy genuine needs and a \"minimalist\" lifestyle of rational consumption, though minimal might not mean boring – it is about what is truly interesting and meaningful.\n\nBy confronting the material desires more rationally, we gain a better understanding of our true needs and become better equipped to care for our own health and well-being. Choose your community, hobbies and a meaningful lifestyle, and draw energy from these things for a healthier, happier life.",
            colors: [
              { hex: "#A8B359", name: "Tender Shoot" },
              { hex: "#9C9339", name: "Seed" },
            ],
          },
          {
            slug: "transmitted-health",
            title: "Transmitted Health",
            image:
              "https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=2970&auto=format&fit=crop",
            description:
              'As technology develops, "treatment" gradually expands to take on a broader meaning. The modern healthcare system expands from formal medical settings to everyday life. Patients receive treatment at home and on the go. Healthcare products and experiences become more pleasant and convenient. Connected devices are essential to this process, by allowing patients to access, share, and analyze medical data and doctors to monitor the health of their patients and plan treatment remotely.\n\nNew treatment models, however, do not come without new challenges and risks. Medical devices must be safe and reliable and patient data secure. Devices must be easy to use and identify, and medical assistance must be readily accessible to patients when needed.\n\nPortable drug delivery systems such as injectors and wearables give patients unprecedented autonomy and control of their treatment and save time and money. In addition, treatment of this kind can be more accessible for patients and cause less disruption to their daily lives.',
            colors: [
              { hex: "#244A77", name: "Hummingbird" },
              { hex: "#DEB9AF", name: "Powder" },
            ],
          },
          {
            slug: "x-dna",
            title: "X-DNA",
            image:
              "https://images.unsplash.com/photo-1677748292704-16cd8b733f8d?q=80&w=3101&auto=format&fit=crop",
            description:
              "Human nature itself drives generation after generation to overcome its own limitations and push the boundaries of longevity. In this new era, the struggle against aging has transcended the stereotypes and physiological constraints of age to achieve greater equality. As lifestyles become more diverse, people no longer fear the decline of physical abilities that comes with aging or the loss of enjoyment in life. Instead, they tend to adopt a positive mindset when facing the aging process. We are living longer and discovering the meaning of a fulfilling life through continuous learning.\n\nTechnology has armed humanity to fight against the limitations imposed upon it by time. As the concept of delaying aging gradually becomes a reality with the help of technology, our perceptions of life and death will change.\n\nRecent studies estimate the global anti-aging market will grow by 6.5% annually between 2023 and 2028, and will reach USD 98.6 billion as people seek more rational, professional, and targeted technology-driven anti-aging solutions against the passage of time. Proponents of transhumanism aim past life expectancy to a healthy lifestyle, employing anti-aging skincare products and medical aesthetics to maintain appearance and health monitoring devices, exercise, and fitness to benefit physiological functions. Cryogenic technology shows us what might be possible under extreme conditions, allowing individuals to transcend the barriers of time in cryogenic sleep and saving genetic information in cryopreservation. Technological support and ethical standards raise our thoughts and behavior to new levels for deeper social connections and emotional exchanges and better understanding of the meaning of human existence as a collective. A new Tower, constructed by seekers of knowledge, reaches out endlessly, challenging eternity and fighting for every second.",
            colors: [
              { hex: "#D1CDCA", name: "Moonlight" },
              { hex: "#C9B5C4", name: "Illusion" },
            ],
          },
          {
            slug: "meta-med",
            title: "Meta Med",
            image:
              "https://images.pexels.com/photos/17483867/pexels-photo-17483867.jpeg?auto=compress&cs=tinysrgb&w=1260",
            description:
              'In the information age, artificial intelligence (AI) technology is impacting every human endeavor in unprecedented ways at unprecedented speeds, and the healthcare industry is no exception. The global population is aging, affecting many industries, and attracting attention to AI-powered healthcare.\n\nAI applications for healthcare can detect early symptoms and reduce treatment times, saving institutions money and improving the efficiency of medical professionals. According to BCG Consulting, "the Meta-Hospital platform is the enterprise guarantee for a variety of clinical applications and the core platform upon which patient data will be integrated in the future." From computer vision technology to upgraded cloud computing power, "meta medicine" is more precise than a human doctor in early disease detection and diagnosis. This demonstrates the unique value of AI technology in drug development, genetic engineering, and digital therapy, among others. Trained algorithms can more efficiently identify rare diseases, decode human genes, and promote medical research in the real world.\n\nThe development of AI technology has made healthcare services more accessible in an uncertain world, where more and more people are seeking a sense of control over their health. The AI-powered future of healthcare lowers barriers to patient care, providing ubiquitous, personalized, and empathetic diagnosis and treatment experiences. Remote diagnosis and treatment technologies alleviate the problem of unequal access to medical resources, enabling more people to receive treatment from AI doctors or human doctors from the comfort of their own homes. The retrieval of cloud data empowers individuals with a more equitable and proactive approach to health management.',
            colors: [
              { hex: "#565957", name: "Outer Space" },
              { hex: "#65A5B6", name: "Glacier" },
            ],
          },
        ],
      },
    ],
    cardDetails: {
      // ── Automotive ──────────────────────────────────────────────────────────
      "ultra-vision": {
        title: "Ultra Vision",
        collection: "Wanderlust · Automotive",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1714548474523-4b075f19ec4a?q=80&w=3132&auto=format&fit=crop",
        description:
          "As digital technology makes increasing headway into smart mobility, autonomous driving technology and intelligent in-car assistants gradually become more widespread.\n\nTechnological development has always prioritized safety. When control is relinquished over the steering wheel and pedals, high-level automation shifts responsibility from the user to the system. The technological question now is how to navigate moral dilemmas where a choice must be made between the two and how to build trust in these vehicles.\n\nVehicles employ visual systems and radar to collect safety data and in-car assistants to communicate with people, who need an audible, visible, interactive co-pilot to share the driving experience with. The panoramic view on the front screen provides the means to take in the real driving environment from multiple perspectives. The information stream can likewise be hidden at a touch when no longer needed, and when the podcast selected by the in-car assistant finishes, the cover lifts to reveal that the journey has reached its destination.\n\nIn any case, safety technology will play a vital role in the future of automation, and emotional connections will raise the concept of human-machine symbiosis to new heights.",
        tags: ["WANDERLUST", "MOBILITY", "FREEDOM"],
        cmfDescription:
          "The pearlescent effect is an external expression of the intelligent system within, marrying wisdom and versatility in blue, while the translucence of the black is modest and restrained, establishing a bridge of trust between users and the machine. High-purity, glossy Makrolon® Ai2457 contrasts with the translucent, homogenous appearance of Makrolon® Ai2417, though both are applied in automotive interiors requiring a special touch.",
        productRender:
          "https://mediahub.covestro.com/transform/ae0fddd5-7e4f-4298-9661-6f7eb5c2e115/2025-CMF-Trends-Aesthetic-Toolkits_MOB1",
        materialSamples: [
          {
            colorHex: "#152838",
            name: "Sparkling Intelligence",
            ralColor: "RAL 250 30 15",
            material: "Makrolon® A2497",
            shape: "Opaque, Pearl",
            appearance: "Gloss Front / Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24G210M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G210M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#2E3031",
            name: "Black Crystal",
            ralColor: "RAL 000 25 00",
            material: "Makrolon® A2417",
            shape: "Transparent",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24G211M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G211M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "moving-castle": {
        title: "Moving Castle",
        collection: "Wanderlust · Automotive",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1515172128886-07c606bb4eb3?q=80&w=3854&auto=format&fit=crop",
        description:
          'With the rise of remote work, more people are choosing to live as digital nomads who find "living in car" offering something a fixed, detached home cannot.\n\nInterior space is taking on the attributes of a real home, liberating our traditional understanding of what makes a home. Increasingly, these tiny mobile homes provide a space for expressing individual personality coupled with the immense freedom of mobility. Transform the interior of your vehicle into a personal home theater, launch yourselves into the depths of space as you project the starry sky onto your ceiling, and recharge and release stress anytime, anywhere. Bring solar panels and camping equipment and take it slow and natural, or join forces with like-minded individuals to form a co-living, exploring creative community writing its own story of liberation. RVs have grown from just another vehicle to embody an artistic lifestyle, where the cozy life on wheels finds meaning in being on the road.\n\nAs the demand for larger RVs continues to increase, brands will also be called upon to fulfill their commitment to the circular economy, adopt more environmentally friendly methods, and use recycled materials and eco-friendly products, empowering people to drive vehicles sourced from green materials towards a greener future.',
        tags: ["WANDERLUST", "MOBILITY", "FREEDOM"],
        cmfDescription:
          "The cockpit is an extension of the larger environment and a personal space of emotional release. The muted gravel with mottled marble resembles a solid wall providing a strong sense of protection, yet it feels like being out in the wild, surrounded by nature's rugged beauty. The bright mint green lightens the overall ambiance with a fresh hue, adding a touch of lightness and a gentle breeze, like a soft, calming whisper. The translucent Makrolon® Ai2497 RE is designed for car and truck interiors requiring a high-purity, glossy surface. The PC+ABS blend Bayblend® T85XUV offers exceptional processability, long-term resistance to humidity and heat, with low VOC and odor. From our CQ portfolio, RE materials are attributed with a certain proportion of recycled raw materials derived from bio-circular feedstock, reducing environmental impact and improving social responsibility with ISCC+ certification.",
        productRender:
          "https://mediahub.covestro.com/transform/2a708b87-9798-47ce-9e8e-3ad1f1930960/2025-CMF-Trends-Aesthetic-Toolkits_MOB2",
        materialSamples: [
          {
            colorHex: "#8B7660",
            name: "Grit",
            ralColor: "RAL 060 50 20",
            material: "Makrolon® A2497 RE",
            shape: "Opaque, Marble",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24G208M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G208M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#93D1CE",
            name: "Horizon Line",
            ralColor: "RAL 190 85 20",
            material: "Bayblend® T85XUV",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24G209B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G209B&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "power-capsule": {
        title: "Power Capsule",
        collection: "Wanderlust · Automotive",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1698913464417-f1971ff59bf1?q=80&w=2964&auto=format&fit=crop",
        description:
          "Concerns about pollution from traditional energy vehicles and rising fuel costs are increasingly turning consumers to more environmentally friendly and economical modes of transportation.\n\nA survey singled out a lack of charging infrastructure as a major obstacle to promoting new energy transport. A more secure, persuasive energy storage solution is required before the era of new energy vehicles can truly take off.\n\nAs energy storage technology moves from industry to the home, a diversity of new solutions are coming to the attention of the automotive industry as well. The energy battery revolution is driving a shift towards environmentally friendly modes of travel with greater economy.",
        tags: ["WANDERLUST", "MOBILITY", "FREEDOM"],
        cmfDescription:
          "Bright orange is ultra visible, projecting vitality and promise and breathing a fresh breath of life into the surroundings. Metallic silver exudes speed and combines functionality with the lightweight aesthetic of cutting-edge technology. Makrolon® FR6005UV is a UV-resistant PC grade, sleek, mechanically superior, and UL94 V0/1.5mm flame retardant. Bayblend® FR3010 achieves a good balance between mechanical properties, heat resistance, and processability and is widely used in network devices and consumer electronic products such as wearables, IoT, smart appliances, and mobile devices among others.",
        productRender:
          "https://mediahub.covestro.com/transform/d2d0278f-4d8d-446b-a19f-6ef00238b918/2025-CMF-Trends-Aesthetic-Toolkits_MOB3",
        materialSamples: [
          {
            colorHex: "#9D9D9D",
            name: "Levitation",
            ralColor: "RAL 000 80 00",
            material: "Makrolon® FR6005UV",
            shape: "Opaque, Metallic",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24G195M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G195M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#EE7541",
            name: "Gravitation Orange",
            ralColor: "RAL 050 60 70",
            material: "Bayblend® FR3010",
            shape: "Opaque",
            appearance: "Matt Back",
            searchUrl:
              "/search?finalColorCode=201351&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=201351&colorSystem=covestro_color_code",
            ),
          },
        ],
      },
      hyperlink: {
        title: "Hyperlink",
        collection: "Wanderlust · Automotive",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1632884333982-e3b4f9fddd0e?q=80&w=3164&auto=format&fit=crop",
        description:
          "Multimodal transportation networks have given us greater autonomy in travel. Therefore, an increasing number of individuals are turning to personalized modes of travel.\n\nGone are the days of limited single-mode public transport. People may now drive or take the subway and then hop on an electric scooter, hoverboard, or electric wheelchair to reach their destinations. Micro-mobility enhances travel efficiency and is more convenient, flexible, and environmentally friendly.\n\nIntegrating shared vehicles into the transportation system will lead to better access to information and optimize transfers between vehicles during the journey. Micro-mobility products have filled the innovation gap for short-distance travel, underserved by public transportation. This also places higher demands on the light weight and foldability of micro-mobility vehicles. Household vehicles need to provide storage space for frequently used vehicles, and even vehicle components can serve as lightweight carriers. As inclusivity spreads to travel, from gender stereotypes to disabilities, designs in the micro-mobility sector will see more inclusive innovations. And finally, urban vertical space itself shows unlimited potential, as low-altitude aircraft break the constraints of concrete jungles and raise transportation services to unprecedented heights. Whether it's Generation Z riding the subway with a skateboard or elderly individuals using electric wheelchairs on buses, the possibilities for travel are boundless.",
        tags: ["WANDERLUST", "MOBILITY", "FREEDOM"],
        cmfDescription:
          "Edge-lit bright lime catches the eye, beckoning towards a futuristic realm and showcasing the ambiguity of a future of optimism. Black contrasts, adding stability and modernity to the overall aesthetic. The PC+ABS blend Bayblend® T85X R25 contains 25% post-consumer recycled polycarbonate with excellent processing qualities.",
        productRender:
          "https://mediahub.covestro.com/transform/0cd5473c-54c6-472f-943a-ec4109ee70c6/2025-CMF-Trends-Aesthetic-Toolkits_MOB4",
        materialSamples: [
          {
            colorHex: "#4D4E50",
            name: "Dark Matter",
            ralColor: "RAL 000 25 00",
            material: "Bayblend® T85X R25A",
            shape: "Opaque",
            appearance: "Matt Front / Gloss Front",
            searchUrl:
              "/search?preliminaryColorCode=24G246B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G246B&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#BED5AE",
            name: "Bright Lime",
            ralColor: "RAL 140 85 10",
            material: "Bayblend® T85X R25A",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24G245B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24G245B&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      // ── Electronics ─────────────────────────────────────────────────────────
      "ai-squad": {
        title: "AI Squad",
        collection: "Aspiration · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://mediahub.covestro.com/transform/abb598ae-b57e-4bae-a359-dfcbaf6394d2/AI-Squad",
        description:
          'Large language models and generative artificial intelligence have exploded onto the scene, thrusting this new technology into our work and everyday lives. As it moves from the laboratory into daily life, a deeper connection between humans and machines must be built if we are to forge greater understanding, trust, and cooperation. When asked what role AI plays at work and in the home, GPT-4 replied that it is our "partner."\n\nIn the workplace, AI has been charged with repetitive tasks like gathering information and organizing data but can also assist in creative tasks like copywriting and image editing, freeing people up to focus on higher value analytical work. Service industry AI can handle mechanical customer service processes even to the point of human interaction requiring emotional intelligence. This efficient working partner, developed through machine learning, will revolutionize the productivity landscape and help organizations address various challenges.\n\nAI in the home is taking on the role of a home assistant. According to a recent poll, 75% of respondents say "please" and "thank you" to their AI assistants, indicative of a desire to establish a more intimate emotional bond with AI. Proactive AI assistants can integrate IoT information flows, provide customized recommendations, and interact with humans through virtual characters or subtle natural language processing. Communication covering all age groups makes the identity of "companion" more diverse: For Generation Alpha, AI-powered education covers classroom learning, smart toys, and interest development; for the elderly, AI provides health monitoring, and support for health and emotional care.',
        tags: ["ASPIRATION", "INNOVATION", "CONNECTIVITY"],
        cmfDescription:
          "Pure white is a staple of electronic product design for the way it bridges the gap between the emerging technology and the physical products with a sense of unadulterated originality that is also gentle and familiar. A transparent texture adds to the lightness sought by smart devices, and the colors shine under the high-gloss finish, integrating complex technology into users' lives in a way that is intuitive and welcoming.\n\nMakrolon® polycarbonate is a tough, lightweight, transparent thermoplastic resin resistant to impact even at low temperatures. It is highly stable, easily molded, and resistant to heat with a glass transition temperature of 148ºC. Makrolon® 2407 is a transparent, high-flow PC grade with good mechanical properties, and is widely used in electronics and electrical products, as well as in lighting and various other industries. Bayblend® PC blends exhibit outstanding and balanced performance, most notably in low-temperature toughness. It also boasts excellent rigidity, dimensional stability, creep resistance, low moisture absorption, heat resistance, and flame retardancy. Bayblend® FR3005HF is a PC/ABS grade with high flowability and flame retardancy (V0/1.5mm). Exceptional processability means a more robust design for customers with excellent colorability to satisfy the most demanding aesthetic requirements.",
        productRender:
          "https://mediahub.covestro.com/transform/d8d972ba-849a-4923-9168-05c90e58d252/2025-CMF-Trends-Aesthetic-Toolkits_EE1",
        materialSamples: [
          {
            colorHex: "#F2F4F1",
            name: "Organdie",
            ralColor: "",
            material: "Makrolon® 2407 RE",
            shape: "Transparent",
            appearance: "Gloss Front / Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24C179M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C179M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#F2F3F3",
            name: "Feather",
            ralColor: "RAL 270 90 05",
            material: "Bayblend® FR3005 HF",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24C157B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C157B&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "digital-wandering": {
        title: "Digital Wandering",
        collection: "Aspiration · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1664776788662-b4ef5d289103?q=80&w=1287&auto=format&fit=crop",
        description:
          "Modern industrial development has shifted the working environments of many from the confines of traditional office space to anywhere with an internet connection. AI technology has taken the new liberty even further, assuming repetitive tasks so that we are free to focus on creativity. The result is an increasing number of individuals choosing to freelance and self-publish and a new definition of working space within a virtual world requiring only that all-important internet connection.\n\nIn addition to changing the way we work, digital technology has also changed the way we think about work. Modern people, who prioritize personal growth over material possessions, have shifted their focus from work to self-fulfillment. Money is still necessary, but there is more of a feeling that sitting in a cubicle to earn a stable salary will not bring spiritual fulfillment or allow one to realize one's full potential.\n\nWith technology, we can now navigate freely between real and virtual worlds, overturning established concepts of space and pushing the boundaries of creativity: meeting with colleagues without ever leaving the comfort of your own home, free to travel to picturesque locations to create, and sharing ideas with the entire community at the touch of a finger.\n\nThe hybrid office has transformed the meaning of productivity and facilitated the integration of diverse living spaces. Immersive technology has gradually been embedded into various office products to provide computing power during work and a comfortable experience in daily life. Multi-functional laptops equipped with better webcams, microphones, and 3D displays replace limitations with a more immersive working experience. Slash workers (people who juggle multiple roles) can multitask more deftly to achieve that delicate work-life balance.\n\nA move towards flexibility in work has led this new generation of office worker to embrace lighter, more scalable portable devices with extended battery life more suited to the agile office of the digital nomad.",
        tags: ["ASPIRATION", "INNOVATION", "CONNECTIVITY"],
        cmfDescription:
          "For the digital nomad passion and inspiration are more than mere embellishments to daily life, they are the essential catalysts driving the creative process. High chroma transparent colors massage the senses. Progressions of pink and red convey vitality and a positive attitude. Overlapping transparencies create a richer atmosphere of colorful personality that resonates with the lifestyle of digital nomads who navigate between the virtual and real worlds.\n\nBayblend® FR3070 R35 contains 35% post-consumer recycled materials, cutting down on the use of fossil raw materials and utilizing plastic waste to benefit a more sustainable circular economy. It is tough and heat resistant, with a Vicat softening point of 105°C (Vicat/B 120) and UL 94 V0/1.2mm certification for flame retardancy ideal for thin-wall designs. The Makrolon® GF9024 is a unique translucent, glass fiber-reinforced flame retardant PC material that satisfies customer demands for high transparency (Ty-80% @ 1mm), high rigidity, and flame retardancy (V0-1.5mm).",
        productRender:
          "https://mediahub.covestro.com/transform/aa66426e-5c8e-47d7-b6c3-1be7b860357e/2025-CMF-Trends-Aesthetic-Toolkits_EE2",
        materialSamples: [
          {
            colorHex: "#791937",
            name: "Berry",
            ralColor: "RAL 360 40 35",
            material: "Bayblend® FR3070 R35",
            shape: "Opaque",
            appearance: "Gloss Front / Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24C158B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C158B&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#8B2224",
            name: "Pomegranate",
            ralColor: "RAL 020 30 48",
            material: "Makrolon® GF9024",
            shape: "Transparent",
            appearance: "Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24C180M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C180M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "boundless-community": {
        title: "Boundless Community",
        collection: "Aspiration · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1712924653565-1cd256dcd2d1?q=80&w=1615&auto=format&fit=crop",
        description:
          'It is common for millennials to live alone in big cities, spend lots of time commuting, and bear the burden of high rents, making co-living in central urban areas worldwide increasingly attractive. Residents get their own private rooms but share facilities like kitchens and bathrooms.\n\nCo-living blurs the boundaries between friend and family, promoting a hybrid family-style model and repositioning housing as a service. It may, in fact, be the ideal lifestyle for young people to have a loved one close at hand, a friend next door, working and living together. Millennials are increasingly embracing cohabitation and capable of filling the need for intimacy with shallow social relationships. Co-living can be a source of freedom and flexibility, though how to balance "private" with the "communal" may place inclusivity on a collision course with the future design of co-living spaces and furnishings.\n\nUnder one roof we live together but seek to retain our independence. The ability to preserve one\'s privacy within limited space is an essential pre-condition to co-living: shared products must be self-cleaning, easy to assemble, convenient to maintain, and able to protect user information and data privacy. As households take on more inhabitants, a personalized experience can lend that human touch we seek. Shared products can remember user preferences and provide services with the requisite emotional value. A purchase from a vending machine may remind you of warm greetings from the owner of the grocery store downstairs.\n\nDiverse public spaces provide residents with opportunities to interact, inspire one another, and attain a higher quality of life with like-minded individuals. By enabling shared living, the "boundless" public spaces have allowed nomadic individuals not only to meet their social needs but also to potentially provide each other with spiritual support.',
        tags: ["ASPIRATION", "INNOVATION", "CONNECTIVITY"],
        cmfDescription:
          "Modular, compatible products unlock new opportunities for application in co-living model. A warm gray is inclusive and welcoming. A misty blue alleviates anxiety, providing a necessary isolation for individuals striving to maintain a delicate balance between their own private world and the world at large. The unique, translucent, fiberglass-reinforced flame-retardant PC material of Makrolon® GF9024 satisfies the most demanding requirements for transparency (Ty-80% @ 1mm), rigidity, and flame retardancy (V0-1.5mm). The Bayblend® FR3010 R75 is 75% PCR in a grade that balances good mechanical properties, heat resistance, and superior processability. It is used in a wide range of consumer electronics, from network devices, wearables, IoT, and smart appliances to mobile devices.",
        productRender:
          "https://mediahub.covestro.com/transform/08212654-7dde-4d03-87ff-7c26b0bd8806/2025-CMF-Trends-Aesthetic-Toolkits_EE3",
        materialSamples: [
          {
            colorHex: "#AFC2C3",
            name: "Foggy Blue",
            ralColor: "RAL 250 85 15",
            material: "Makrolon® GF9024",
            shape: "Transparent",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24C175M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C175M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#CDCAC4",
            name: "Smoky Gray",
            ralColor: "RAL 000 85 00",
            material: "Bayblend® FR3010 R75",
            shape: "Opaque",
            appearance: "Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24C169B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C169B&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "tech-nature": {
        title: "Tech × Nature",
        collection: "Aspiration · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1593586710811-c92ee75ddf6d?q=80&w=3174&auto=format&fit=crop",
        description:
          "With increasing environmental awareness, the pursuit of sustainable living is not just a trend but has become our habit. Cherishing natural resources and protecting nature's gifts have become priorities. More and more individuals today are rethinking their life priorities and proactively changing their mindset to think is there a new way to combine technology and nature. An increasing number of individuals today are embracing a minimalist lifestyle and are starting to realize that technology and nature are not necessarily opposing ends. Is it possible for the two to work together in a sustainable way?\n\nInnovative spatial ecosystems can bring net-positive energy within reach, making our households more self-sufficient and independent of the national power grid. Stand-alone energy cells can be integrated seamlessly and organically into daily life, combining the aesthetic and the functional. Whether bold or understated, they can imbue our homes with aesthetic enjoyment and responsible, eco-friendly living.",
        tags: ["ASPIRATION", "INNOVATION", "CONNECTIVITY"],
        cmfDescription:
          "On the road to coexistence with nature, we must learn to balance design aesthetics with practicality and make sustainable living more attractive. The yellow hue is one of endless vitality and the awakening of a greater consciousness of nature. Forest-like green provides a sanctuary for trust and healing, inviting people to take some rest and return to authenticity.\n\nMakrolon® FR6020 and Makrolon® FR6005UV are UV-resistant and flame retardant (UL94 V0/1.5mm). Makrolon® FR6020 is good for use outdoors (f1, 5VA) in base and charging stations. Makrolon® FR6020 and Makrolon® FR6005UV open the door to the wider possibilities of aesthetic design, with mechanical performance and flame retardancy to meet increasing demands for functionality and safety.",
        productRender:
          "https://mediahub.covestro.com/transform/39d377c9-a214-40fe-bc37-3d3b1728b91c/2025-CMF-Trends-Aesthetic-Toolkits_EE4",
        materialSamples: [
          {
            colorHex: "#657B5D",
            name: "Oasis",
            ralColor: "RAL 140 40 30",
            material: "Makrolon® FR6020",
            shape: "Opaque",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24C140M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C140M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#C0B76A",
            name: "Wheat Field",
            ralColor: "RAL 095 85 50",
            material: "Makrolon® FR6005UV",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24C141M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24C141M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      // ── Healthcare ──────────────────────────────────────────────────────────
      "cozy-groove": {
        title: "Cozy Groove",
        collection: "Hyperspace · Healthcare",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.pexels.com/photos/7241294/pexels-photo-7241294.jpeg?auto=compress&w=1920",
        description:
          "We live in a society obsessed with efficiency, with bodies so busy there's hardly a second left for the soul. The result of this new life is a numbness that leaves us unable to empathize with others, incapable of self-awareness, overloaded emotionally, and increasingly isolated. And finally, freedom and tolerance have become new themes of thought, prompting people to reconsider alternative lifestyles beyond mainstream values.\n\nThe new generation, represented by Generation Z, tends to follow their inner preferences and freely express their personal style in life: whether it's adding their own touch to home decor through handmade projects, or finding relaxation and comfort amidst hand-planted greenery. Their focus on the personal life exceeds that of previous generations, as does their pursuit of value for money. Young people are embracing practical products to satisfy genuine needs and a \"minimalist\" lifestyle of rational consumption, though minimal might not mean boring – it is about what is truly interesting and meaningful.\n\nBy confronting the material desires more rationally, we gain a better understanding of our true needs and become better equipped to care for our own health and well-being. Choose your community, hobbies and a meaningful lifestyle, and draw energy from these things for a healthier, happier life.",
        tags: ["HYPERSPACE", "WELLBEING", "TRUST"],
        cmfDescription:
          "The bright chartreuse is soft and fresh like tender shoots, soothing the soul. The breathable texture in soothing yellow conveys warmth and inspires trust. Makroblend® M525 is a skin-friendly polycarbonate polyester blend resistant to household chemicals that meets ISO 10993-5 requirements for in vitro cytotoxicity and ISO 10993-10 tests for irritation and delayed hypersensitivity. Medical-grade Makrolon® M6011FR PC meets ISO 10993-5 requirements for in vitro cytotoxicity and ISO 10993-10 tests for irritation and delayed hypersensitivity. It is UL94 V-0 (1.5mm) flame retardant, resistant to household chemicals and disinfectants, and UV and impact resistant at medium viscosity. From our CQ portfolio of more sustainable options, RE materials are attributed with a proportion of recycled raw materials derived from bio-circular feedstock, reducing environmental impact and improving social responsibility with ISCC+ certification.",
        productRender:
          "https://mediahub.covestro.com/transform/11e67fab-2fc7-4764-87c6-9f4f8b655098/2025-CMF-Trends-Aesthetic-Toolkits_HC1",
        materialSamples: [
          {
            colorHex: "#A8B359",
            name: "Tender Shoot",
            ralColor: "RAL 110 85 50",
            material: "Makroblend® M525",
            shape: "Opaque",
            appearance: "Gloss Front / Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24M098Z&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M098Z&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#9C9339",
            name: "Seed",
            ralColor: "RAL 095 70 50",
            material: "Makrolon® M6011FR RE",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M094M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M094M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "transmitted-health": {
        title: "Transmitted Health",
        collection: "Hyperspace · Healthcare",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=2970&auto=format&fit=crop",
        description:
          'As technology develops, "treatment" gradually expands to take on a broader meaning. The modern healthcare system expands from formal medical settings to everyday life. Patients receive treatment at home and on the go. Healthcare products and experiences become more pleasant and convenient. Connected devices are essential to this process, by allowing patients to access, share, and analyze medical data and doctors to monitor the health of their patients and plan treatment remotely.\n\nNew treatment models, however, do not come without new challenges and risks. Medical devices must be safe and reliable and patient data secure. Devices must be easy to use and identify, and medical assistance must be readily accessible to patients when needed.\n\nPortable drug delivery systems such as injectors and wearables give patients unprecedented autonomy and control of their treatment and save time and money. In addition, treatment of this kind can be more accessible for patients and cause less disruption to their daily lives.',
        tags: ["HYPERSPACE", "WELLBEING", "TRUST"],
        cmfDescription:
          'The diversity of possible medical scenarios means products must focus on both functionality and emotion. The purity of blue draws attention to the professional, safe, reliable nature of the product, while the vibrancy of orange adds a sense of vitality. (PC+ABS) blend Bayblend® M850 XF is a high-flow medical grade that is ISO 10993-1 biocompatible. The renewable equivalent material, denoted by "RE," is offered as part of our CQ portfolio. RE materials are attributed with a proportion of recycled raw materials derived from bio-circular feedstock, reducing environmental impact and improving social responsibility with ISCC+ certification.',
        productRender:
          "https://mediahub.covestro.com/transform/2c19a493-f6d8-48f6-93ab-65fa82462654/2025-CMF-Trends-Aesthetic-Toolkits_HC2",
        materialSamples: [
          {
            colorHex: "#244A77",
            name: "Hummingbird",
            ralColor: "RAL 260 50 35",
            material: "Bayblend® M850 XF RE",
            shape: "Opaque",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M097B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M097B&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#DEB9AF",
            name: "Powder",
            ralColor: "RAL 050 85 10",
            material: "Bayblend® M850 XF RE",
            shape: "Opaque",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M096B&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M096B&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "x-dna": {
        title: "X-DNA",
        collection: "Hyperspace · Healthcare",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.unsplash.com/photo-1677748292704-16cd8b733f8d?q=80&w=3101&auto=format&fit=crop",
        description:
          "Human nature itself drives generation after generation to overcome its own limitations and push the boundaries of longevity. In this new era, the struggle against aging has transcended the stereotypes and physiological constraints of age to achieve greater equality. As lifestyles become more diverse, people no longer fear the decline of physical abilities that comes with aging or the loss of enjoyment in life. Instead, they tend to adopt a positive mindset when facing the aging process. We are living longer and discovering the meaning of a fulfilling life through continuous learning.\n\nTechnology has armed humanity to fight against the limitations imposed upon it by time. As the concept of delaying aging gradually becomes a reality with the help of technology, our perceptions of life and death will change.\n\nRecent studies estimate the global anti-aging market will grow by 6.5% annually between 2023 and 2028, and will reach USD 98.6 billion as people seek more rational, professional, and targeted technology-driven anti-aging solutions against the passage of time. Proponents of transhumanism aim past life expectancy to a healthy lifestyle, employing anti-aging skincare products and medical aesthetics to maintain appearance and health monitoring devices, exercise, and fitness to benefit physiological functions. Cryogenic technology shows us what might be possible under extreme conditions, allowing individuals to transcend the barriers of time in cryogenic sleep and saving genetic information in cryopreservation. Technological support and ethical standards raise our thoughts and behavior to new levels for deeper social connections and emotional exchanges and better understanding of the meaning of human existence as a collective. A new Tower, constructed by seekers of knowledge, reaches out endlessly, challenging eternity and fighting for every second.",
        tags: ["HYPERSPACE", "WELLBEING", "TRUST"],
        cmfDescription:
          "Translucid purple embodies mystery and the spirit of exploration driving humanity to overcome its own limitations. Paired with a cool white reminiscent of moonlight, it showcases the power of technology working together with endless imagination.\n\nMakrolon® Rx1805 is a medical-grade polycarbonate that is ISO 10993-1 biocompatible. It is suitable for sterilization with high-energy radiation, ethylene oxide, or steam, and offers chemical/lipid resistance and high strength. From our CQ portfolio, RE materials are attributed with a certain proportion of recycled raw materials derived from bio-circular feedstock, reducing environmental impact and improving social responsibility with ISCC+ certification.",
        productRender:
          "https://mediahub.covestro.com/transform/72ee0eba-7b70-4bd5-9622-f543bb91570d/2025-CMF-Trends-Aesthetic-Toolkits_HC3",
        materialSamples: [
          {
            colorHex: "#D1CDCA",
            name: "Moonlight",
            ralColor: "RAL 9003",
            material: "Makrolon® Rx1805 RE",
            shape: "Opaque",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M095M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M095M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#C9B5C4",
            name: "Illusion",
            ralColor: "RAL 300 80 10",
            material: "Makrolon® Rx1805 RE",
            shape: "Translucid",
            appearance: "Matt Back",
            searchUrl:
              "/search?preliminaryColorCode=24M100M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M100M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
      "meta-med": {
        title: "Meta Med",
        collection: "CMF Design Trends 2025+ Edition",
        collectionSlug: "cmf-design-trends-2025",
        heroImage:
          "https://images.pexels.com/photos/17483867/pexels-photo-17483867.jpeg?auto=compress&w=1920",
        description:
          'In the information age, artificial intelligence (AI) technology is impacting every human endeavor in unprecedented ways at unprecedented speeds, and the healthcare industry is no exception. The global population is aging, affecting many industries, and attracting attention to AI-powered healthcare.\n\nAI applications for healthcare can detect early symptoms and reduce treatment times, saving institutions money and improving the efficiency of medical professionals. According to BCG Consulting, "the Meta-Hospital platform is the enterprise guarantee for a variety of clinical applications and the core platform upon which patient data will be integrated in the future." From computer vision technology to upgraded cloud computing power, "meta medicine" is more precise than a human doctor in early disease detection and diagnosis. This demonstrates the unique value of AI technology in drug development, genetic engineering, and digital therapy, among others. Trained algorithms can more efficiently identify rare diseases, decode human genes, and promote medical research in the real world.\n\nThe development of AI technology has made healthcare services more accessible in an uncertain world, where more and more people are seeking a sense of control over their health. The AI-powered future of healthcare lowers barriers to patient care, providing ubiquitous, personalized, and empathetic diagnosis and treatment experiences. Remote diagnosis and treatment technologies alleviate the problem of unequal access to medical resources, enabling more people to receive treatment from AI doctors or human doctors from the comfort of their own homes. The retrieval of cloud data empowers individuals with a more equitable and proactive approach to health management.',
        tags: ["HYPERSPACE", "WELLBEING", "TRUST"],
        cmfDescription:
          "The cold matte gray reflects the reliability of the product and sets off the transparent blue of the embedded chips. The gray of the back panel conveys trust and contrasts aesthetically with the blue, which represents intelligence, continually guiding user interaction and enhancing the product's clarity and sense of control. Makrolon® 2458 is a medical-grade polycarbonate that is ISO 10993-1 biocompatible. It is suitable for ethylene oxide or steam sterilization, with low viscosity for easy demolding. From our CQ portfolio, RE materials are attributed with a certain proportion of recycled raw materials derived from bio-circular feedstock, reducing environmental impact and improving social responsibility with ISCC+ certification.",
        productRender:
          "https://mediahub.covestro.com/transform/48c8fc4e-3ee5-4017-8fdb-fb1e905913a7/2025-CMF-Trends-Aesthetic-Toolkits_HC4",
        materialSamples: [
          {
            colorHex: "#565957",
            name: "Outer Space",
            ralColor: "RAL 260 40 05",
            material: "Makrolon® 2458 RE",
            shape: "Opaque",
            appearance: "Matt Front / Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M099M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M099M&colorSystem=preliminary_color_code",
            ),
          },
          {
            colorHex: "#65A5B6",
            name: "Glacier",
            ralColor: "RAL 230 70 30",
            material: "Makrolon® 2458 RE",
            shape: "Translucid",
            appearance: "Gloss Back",
            searchUrl:
              "/search?preliminaryColorCode=24M101M&colorSystem=preliminary_color_code",
            link: imagioLink(
              "/search?preliminaryColorCode=24M101M&colorSystem=preliminary_color_code",
            ),
          },
        ],
      },
    },
  },

  // ── CMF Design Trends 2022 | 2023 Edition (CX-2790) ──────────────────────
  "cmf-design-trends-2022-2023": {
    slug: "cmf-design-trends-2022-2023",
    title: "CMF Design Trends 2022 | 2023 Edition",
    heroImage: "/imgs/trend/cmf20223-hero.jpg",
    tagLabel: "CMF 2022 | 2023",
    themes: ["AWAKENING", "COEXISTENCE", "EMPOWERMENT"],
    introText: [
      "No longer the simple assembly-line produced metal boxes of yesteryear; today's automobiles are sophisticated machines that have surpassed their original purpose of transporting passengers from one destination to another. Vehicles are not only designed for meaningful integration of space and functionality but also viewed as a means of solving everyday problems and alleviating anxiety. As it becomes easier to mimic current designs, finding unique sources of inspiration can prove difficult – as such, many are looking to various cultural origins to gain fresh insights and perspectives. Within the symbols, patterns, and other hallmarks of early civilizations, it may be possible to uncover new and innovative blueprints for future mobility. It is also worth noting that automotive developments are moving from purely industrial pursuits towards more elevated, design-led approaches, driven by the considerable influence and purchasing behavior of younger consumers. As the scope for interaction between user and vehicle continues to expand and evolve, so do the requirements for automotive materials. Contemporary material innovations have brought about greater design possibilities from better personalization, to lightweighting and recyclable materials enabling sustainable development in the process. As the future CMF trends are mainly oriented to the interior, we focus these new proposal on the interior application.",
      "Human evolutionary progress can be attributed to a continuous pursuit of self-improvement as well as the ability to embrace diversity and adapt to new environments. Humankind is currently in the midst of its next phase of transformation: humans are approaching a confluence with machines. How should virtual and physical worlds be integrated with one another? Can humans live in balanced harmony with nature – is it a concept destined to remain science fiction, or a feasible future reality? In high-density areas, micro design solutions are being used to optimize the functionality of compact living spaces in order to cater to the human need for spaciousness and comfort. Demand for smart and connected home appliances will continue to flourish, as multifunctional, adaptable materials and bespoke aesthetics gradually replace disparate design styles and products. Rapid advancements in network technology have accelerated the transmission of information, with greater speed and accuracy enabled by large databases. However, this momentum has also contributed to the spread of fear and anxiety over privacy-related issues and new generations of product designers must address security concerns too. 'Going green' is not just a slogan, but an essential course of action that should be applied to our lives on a daily basis. Clean energy, recyclable materials and non-electric-powered appliances are some of the ways in which we can restore the health of our planet while making product and lifestyle improvements. It is with the hope of achieving a balanced coexistence with nature that we continue our efforts in these areas.",
      "Today's world presents many challenges and we often find ourselves asking how can we ensure all people enjoy an equal, improved quality of life. While we know all living beings are continuously aging, on a broader spectrum, human existence carries with it the promise of continuous improvement. As the human lifespan lengthens, the very definition and boundaries of life are also evolving and expanding. With the prevalence of long-term and chronic diseases on the rise, many now find themselves living with expanded definitions of what it means to be and feel well. As populations grow and change, medical technology and devices will be needed to support highly varied groups of people. As recent events have shown, a globally-minded community is more important than ever. Through pandemics, environmental challenges and more, many things may be out of our daily control – however there is still an opportunity to work together collaboratively to preserve the health of our communities and environment.",
    ],
    quote:
      "«Design is not just about aesthetics; it is about creating meaningful connections between materials, people, and the environment — challenging us to see circularity not as a constraint, but as the most powerful design brief of our time.»",
    sections: [
      {
        title: "Automotive",
        themes: ["AWAKENING"],
        heroImage: "/imgs/trend/cmf20223-automotive.jpg",
        introText: [
          "No longer the simple assembly-line produced metal boxes of yesteryear; today’s automobiles are sophisticated machines that have surpassed their original purpose of transporting passengers from one destination to another. Vehicles are not only designed for meaningful integration of space and functionality but also viewed as a means of solving everyday problems and alleviating anxiety. As it becomes easier to mimic current designs, finding unique sources of inspiration can prove difficult – as such, many are looking to various cultural origins to gain fresh insights and perspectives.",
        ],
        cards: [
          {
            slug: "space-at-pace",
            title: "Space at Pace",
            image: "/imgs/trend/ace-oasis-of-freedom.png",
            description:
              "In the hustle and bustle of the city, the pace of urban life fragments time into fleeting moments, with enormous pressure to shift quickly from one context to another. Here, automobiles could become adaptive spaces to help manage these transitions, as interiors become more focused on pleasurable spatial experiences instead of technological displays. Users could fully immerse themselves in the relaxing, restorative environment automatically generated by the car and be rejuvenated.\n\nSuch spaces would provide a safe harbor, where users can enjoy being in a soothing, comfortable, and clean environment.",
            colors: [
              { hex: "#E8D8C8", name: "Cocoons" },
              { hex: "#D8C0A0", name: "Warmth" },
              { hex: "#A8D8C0", name: "Peppermint" },
              { hex: "#D0D0D0", name: "Light Grey" },
            ],
          },
          {
            slug: "distant-realms",
            title: "Distant Realms",
            image: "/imgs/trend/ace-multicultural-inspiration.png",
            description:
              "Globalization has made the world smaller: the same stores appearing on every street corner, familiar skyscrapers dominating city skylines across the globe. In an era of instant information, have our lives all become indistinguishable from one another? Perhaps it is possible to break from this monotony by looking to past civilizations — authentic, unembellished colors and materials could be the key to unlocking greater imagination and creativity in mobility design.\n\nIt is for these reasons that age-old cultures have long been a source of inspiration in automotive interiors. Their contribution is far from superficial; instead, they provide a rich cache of cultural knowledge, with complex concepts from philosophy to cosmology woven into symbols, patterns, and colors.",
            colors: [
              { hex: "#1A1A3A", name: "Indigo" },
              { hex: "#C8A878", name: "Desert" },
              { hex: "#4A6848", name: "Wild Peak" },
              { hex: "#F0A0B8", name: "Wildflower" },
            ],
          },
          {
            slug: "meaningful-autonomy",
            title: "Meaningful Autonomy",
            image: "/imgs/trend/ace-self-expression-reigns-supreme.png",
            description:
              "Young people are considered the lifeblood of urban development, but this consumer segment itself is also highly multifaceted. Any lifestyle is possible — single, DINK, freelancing, or being a part of the ‘Slash Generation’ — as long as you take responsibility for your choices. In contrast to the relatively conservative and prudent spending patterns of their parents, Generation Z have higher disposable incomes and are more willing to spend.\n\nCars are products of high practical value — providing mobility and the conveniences that come with it, but they can also be considered as works of art that display their owners’ tastes. Eager for creative encounters and outlets, the younger generation may look to automotive designs that enable them to discover, shape and express their individual identities in a meaningful way.",
            colors: [
              { hex: "#C83020", name: "Tomato" },
              { hex: "#E04060", name: "Watermelon" },
              { hex: "#D0E0E8", name: "Frost" },
              { hex: "#E8A020", name: "Mango" },
            ],
          },
          {
            slug: "the-next-destination",
            title: "The Next Destination",
            image: "/imgs/trend/ace-lightweight-resilience.png",
            description:
              "Since its genesis, the automobile has undergone significant change in terms of exterior structure, functionality, and even aesthetic direction. Will the next wave of material and structural innovations pave the way for the next phase of automotive evolution? Current exploration of alternative materials and clean energy seems to indicate that cars will still have a place in future sustainable habitats.\n\nWith flexibility, cleanliness and safety in mind, the goal is to build a lightweight, energy-efficient solution — so that each time the engine runs, it casts less of a shadow on the environment.",
            colors: [
              { hex: "#1A1A2A", name: "Obsidian" },
              { hex: "#A0A0A8", name: "Mineral Grey" },
              { hex: "#C87840", name: "Tawny" },
              { hex: "#D8B870", name: "Golden Sand" },
            ],
          },
        ],
      },
      {
        title: "Electronics & Electrical",
        themes: ["COEXISTENCE"],
        heroImage: "/imgs/trend/cmf20223-electronics.jpg",
        introText: [
          "Human evolutionary progress can be attributed to a continuous pursuit of self-improvement as well as the ability to embrace diversity and adapt to new environments. Humankind is currently in the midst of its next phase of transformation: humans are approaching a confluence with machines. How should virtual and physical worlds be integrated with one another? Can humans live in balanced harmony with nature – is it a concept destined to remain science fiction, or a feasible future reality?",
        ],
        cards: [
          {
            slug: "superhuman-2-5g",
            title: "Superhuman 2.5G",
            image: "/imgs/trend/ace-personalized-interaction.jpg",
            description:
              "Humans have always been complex creatures, imbued with a blend of rational and emotional sensibilities that compel them to continually seek self-improvement. From skin contact and limb mobility to brain function, our bodies comprise extremely sophisticated mechanisms that require consistent maintenance, conscious or otherwise. Through meticulous research, analysis and mimicry of human characteristics, advanced technologies have come close to being able to reproduce interactions such as touch, temperature sensing and neurological activity.\n\nWith time, such developments are expected to enhance quality of life, augmenting our natural abilities whilst also making experiences more enjoyable. With the rapid advent of microcomputers and neuroscience, could the superhuman abilities imagined in science fiction films become reality?",
            colors: [
              { hex: "#2DB87A", name: "Aurora Green" },
              { hex: "#E88CA0", name: "Particle Pink" },
              { hex: "#4A5A7A", name: "Super Power" },
              { hex: "#D8D0C8", name: "Light Speed" },
            ],
          },
          {
            slug: "a-multifaceted-existence",
            title: "A Multifaceted Existence",
            image: "/imgs/trend/ace-fragmented-lifestyles.png",
            description:
              "With the aggressive expansion of urban environments and exponential increase in global population, our natural resources are in decline. To combat this, we need smarter products in everyday life that can extend what is available and accommodate more possibilities.\n\nAs urban living spaces become smaller and smaller, multifunctional products are needed — items that are modular and can be easily adapted to accommodate varying scenarios, from recreation to work. Smart appliances and IoT-enabled modular home decorations represent a convergence of technology with everyday living.",
            colors: [
              { hex: "#1A1A1A", name: "Calm Black" },
              { hex: "#C8B898", name: "Gauze Curtain" },
              { hex: "#8B7840", name: "Fine Gold" },
              { hex: "#D0E8D0", name: "Halo" },
            ],
          },
          {
            slug: "transparent-society",
            title: "Transparent Society",
            image: "/imgs/trend/ace-privacy-filters.png",
            description:
              "Information technology has transformed our world into a vast database. Under its gaze, everything is transparent. Our eating and drinking habits, interests and hobbies can be tracked and discovered in swathes of code.\n\nProducts must navigate a delicate balance between openness and intimacy, security and freedom. As megacities generate ever-more data, finding ways to protect privacy while benefiting from transparency becomes one of the defining challenges of modern design.",
            colors: [
              { hex: "#A0A0A8", name: "Foggy Grey" },
              { hex: "#9080B8", name: "Lavender" },
              { hex: "#5A3A7A", name: "Purple Dragonfly" },
              { hex: "#C87040", name: "Amber" },
            ],
          },
          {
            slug: "conscious-consumption",
            title: "Conscious Consumption",
            image: "/imgs/trend/ace-purposeful-living.png",
            description:
              "One of the main points of 2022 and beyond will be to conserve the earth’s resources and avoid excessive consumption. In response to ecological issues, materials such as recyclable plastics and biodegradables are being developed at an accelerated pace, while clean, green sources of energy are also being deployed.\n\nHumans are beginning to view industrial products made from recycled materials as desirable, not just necessary. ‘Going green’ is not just a slogan, but an essential course of action applied to our lives on a daily basis.",
            colors: [
              { hex: "#60B8A8", name: "Eco Teal" },
              { hex: "#A8D8C8", name: "Fresh Mint" },
              { hex: "#D0E8D8", name: "Light Green" },
            ],
          },
        ],
      },
      {
        title: "Healthcare",
        themes: ["EMPOWERMENT"],
        heroImage: "/imgs/trend/cmf20223-healthcare.jpg",
        introText: [
          "Today’s world presents many challenges and we often find ourselves asking how can we ensure all people enjoy an equal, improved quality of life. As the human lifespan lengthens, the very definition and boundaries of life are also evolving and expanding. With the prevalence of long-term and chronic diseases on the rise, many now find themselves living with expanded definitions of what it means to be and feel well.",
        ],
        cards: [
          {
            slug: "intelligent-interaction",
            title: "Intelligent Interaction",
            image: "/imgs/trend/ace-discreet-biomimicry.png",
            description:
              "With the development of artificial intelligence (AI), healthcare and wellness devices have created new possibilities for maintaining daily health. AI powered devices can provide daily at-home medical services, identifying symptoms and other medical information through patient interaction, providing medical professionals with more accurate data, making the diagnosis process easier and more efficient.\n\nThese intelligent designs not only interpret and manage symptoms, but can also provide a level of emotional comfort for a more pleasant treatment experience.",
            colors: [
              { hex: "#E8B8A0", name: "Kitten’s Footprint" },
              { hex: "#F0D0C0", name: "Milk Candy" },
            ],
          },
          {
            slug: "inclusive-well-being",
            title: "Inclusive Well-being",
            image: "/imgs/trend/ace-connected-communities.png",
            description:
              "As society continues to evolve and change, the daily structure of work and homelife has shifted family structures and responsibilities. With an aging population, there is a growing need for devices which can bridge the gap between the experiences of the young and old.\n\nMultiple demographic shifts are generating opportunities for growth and progress: young consumers choosing to live alone, older individuals wanting to indulge in the present, and others placing greater value on self-improvement.",
            colors: [
              { hex: "#3A3A6A", name: "Purple Blue" },
              { hex: "#C870A0", name: "Lustrous Pink" },
            ],
          },
          {
            slug: "empathy-amplified",
            title: "Empathy Amplified",
            image: "/imgs/trend/ace-sensorial-clarity.png",
            description:
              "Many individuals seek to maintain their physical health, but youthful health and energy is difficult to sustain as one grows older. Now with modern medicine, current concerns are not necessarily about longevity, but instead focus on the quality and dignity of life as people age.\n\nTechnology and devices offer solutions to mitigate some of the physiological deterioration which usually occurs later in life. Bright, vivid high-intensity colors in strong contrasting combinations can serve as warning signs or provide vulnerable users with greater confidence.",
            colors: [
              { hex: "#4AB89A", name: "Apple Green" },
              { hex: "#F06020", name: "Orange Glow" },
            ],
          },
          {
            slug: "resilient-optimism",
            title: "Resilient Optimism",
            image: "/imgs/trend/ace-smart-sustainability.png",
            description:
              "Recent events such as unprecedented wildfires, hurricanes, winter storms and heatwaves have renewed our attention on environmental conditions. These events are awakening us to examine and modify our behaviors otherwise our actions may cause unintended consequences.\n\nA globally-minded community is more important than ever. Through pandemics, environmental challenges and more, there is still an opportunity to work together collaboratively using reusable materials and clean energy.",
            colors: [
              { hex: "#7A3A2A", name: "Resilient Brown" },
              { hex: "#C89070", name: "Warm Earth" },
            ],
          },
        ],
      },
    ],
    cardDetails: {
      // ── Automotive ──────────────────────────────────────────────────────────
      "space-at-pace": {
        title: "Space at Pace",
        collection: "Awakening · Automotive",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-oasis-of-freedom.png",
        description:
          "In the hustle and bustle of the city, the pace of urban life fragments time into fleeting moments, with enormous pressure to shift quickly from one context to another. Here, automobiles could become adaptive spaces to help manage these transitions, as interiors become more focused on pleasurable spatial experiences instead of technological displays. Users could fully immerse themselves in the relaxing, restorative environment automatically generated by the car and be rejuvenated.\n\nSuch spaces would provide a safe harbor, where users can enjoy being in a soothing, comfortable, and clean environment.",
        tags: ["AUTOMOTIVE", "AWAKENING", "MAKROLON®"],
        cmfDescription:
          "Warm shades of white and translucent blush tones create the feeling of being in a safe, snug haven where one can let go and fully unwind, while bubble textures add to the sense of being able to breathe freely. Makrolon® Ai2497, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior. Light and natural hues bring about a soft, calming ambience, while textures that resemble rippling water create a sense of flowing movement. Makrolon® Ai2457, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior.",
        subsections: [
          {
            title: "Warm Embrace",
            text: "Whether it's associating sound with surface tactility, or temperature with geometry, the combination of multiple sensory stimuli can reshape our understanding of color, shape, structure, and texture. Multisensory perception can enrich our experiences in ways that are reassuring or comforting, like a mother's embrace might be to a child. Inclusive design ensures that these experiences are available for individuals regardless of physical impairment.",
          },
          {
            title: "Oasis of Freedom",
            text: "Over time, cars have provided people with the ability to travel far and wide at speed. Today's consumers want personalized spaces, separate from an office or residence; somewhere to relax, have a little breathing space and allow one's thoughts to wander, whilst secure in the knowledge that the vehicle is monitoring their health data for safe driving. As work and leisure boundaries continue to merge, the car becomes a home extension – a place to work, play, rest and socialize.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-space-at-pace-chip1.png",
            samples: [
              {
                colorHex: "#F5E6D0",
                name: "Cocoons",
                ralColor: "RAL 1013",
                material: "Makrolon® 2807",
                shape: "Solid",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=010414&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=010414&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#E8B8A0",
                name: "Warmth",
                ralColor: "RAL 060 70 20",
                material: "Makrolon® 2405",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=820002&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=820002&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-space-at-pace-chip2.png",
            samples: [
              {
                colorHex: "#B8DED0",
                name: "Peppermint",
                ralColor: "RAL 200 80 10",
                material: "Makrolon® 2405",
                shape: "Translucent",
                appearance: "Gloss Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=620002&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=620002&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#D8D8D8",
                name: "Light Grey",
                ralColor: "RAL 860-1",
                material: "Makrolon® 2405",
                shape: "Translucent",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=720018&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=720018&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "distant-realms": {
        title: "Distant Realms",
        collection: "Awakening · Automotive",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-multicultural-inspiration.png",
        description:
          "Globalization has made the world smaller: the same stores appearing on every street corner, familiar skyscrapers dominating city skylines across the globe. In an era of instant information, have our lives all become indistinguishable from one another? Perhaps it is possible to break from this monotony by looking to past civilizations — authentic, unembellished colors and materials could be the key to unlocking greater imagination and creativity in mobility design.\n\nIt is for these reasons that age-old cultures have long been a source of inspiration in automotive interiors. Their contribution is far from superficial; instead, they provide a rich cache of cultural knowledge, with complex concepts from philosophy to cosmology woven into symbols, patterns, and colors.",
        tags: ["AUTOMOTIVE", "AWAKENING", "BAYBLEND®", "MAKROLON®"],
        cmfDescription:
          "Cultural immersion and understanding can be an effective antidote to mediocre design. Boldly contrasting color combinations and shades reminiscent of natural elements such as wind, water and soil are ideal. 3D printing and other technologies are ushering in a new era of celebrating cultural traditions and craftsmanship, ensuring the continued existence of one-of-a-kind, handmade products. Bayblend® T85 XUV, a UV-stable (PC+ABS) blend with good chemical resistance, ideally suited for decorative interior components. Makrolon® AI2497, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior.",
        subsections: [
          {
            title: "Multicultural Inspiration",
            text: "Through the study of other cultures and ethnicities, today's urbanites find an escape from the tedium of daily life, distancing themselves from reality whilst broadening their horizons. Automotive design takes cues from this approach by reinterpreting historical emblems and narratives to form novel concepts instead of replicating generic ones.",
          },
          {
            title: "Rethinking Craftsmanship",
            text: "While standardization across the automotive industry has greatly increased precision in manufacturing, this efficiency can result in outputs that feel cold and impersonal. Can design be used to convey a more human touch, by bringing together craft techniques and technology in different ways? This combined approach could carve out new narratives for material design and development. With the development of 3D printing and other technologies, it is possible to replicate and celebrate cultural traditions, ensuring the continued existence of one-of-a-kind, handmade products.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-distant-realms-chip1.png",
            samples: [
              {
                colorHex: "#2A3040",
                name: "Indigo",
                ralColor: "RAL 5008",
                material: "Bayblend® T85 XUV",
                shape: "Solid",
                appearance: "Matt Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=500193&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=500193&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#8B5A2B",
                name: "Desert",
                ralColor: "RAL 060 60 30",
                material: "Makrolon® AI2497",
                shape: "Solid, Pearl",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=830003&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=830003&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-distant-realms-chip2.png",
            samples: [
              {
                colorHex: "#2A2A2A",
                name: "Wild Peak",
                ralColor: "RAL 7026",
                material: "Bayblend® T85 XUV",
                shape: "Solid",
                appearance: "Matt Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=600103&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=600103&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#F0A0B8",
                name: "Wildflower",
                ralColor: "RAL 010 90 15",
                material: "Bayblend® T85 XUV",
                shape: "Solid",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=300122&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=300122&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-distant-realms-chip3.png",
            samples: [
              {
                colorHex: "#1A1A1A",
                name: "Kintsugi",
                ralColor: "RAL 9005",
                material: "Makrolon® Ai2497",
                shape: "Solid",
                appearance: "Gloss Front / Gloss Back",
              },
              {
                colorHex: "#4A4A4A",
                name: "Sculpture",
                ralColor: "-",
                material: "PC 3D printing",
                shape: "-",
                appearance: "3D printed",
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "meaningful-autonomy": {
        title: "Meaningful Autonomy",
        collection: "Awakening · Automotive",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-self-expression-reigns-supreme.png",
        description:
          "Young people are considered the lifeblood of urban development, but this consumer segment itself is also highly multifaceted. Any lifestyle is possible – single, DINK, freelancing, or being a part of the 'Slash Generation' – as long as you take responsibility for your choices. In contrast to the relatively conservative and prudent spending patterns of their parents, Generation Z have higher disposable incomes and are more willing to spend. Cars are products of high practical value– providing mobility and the conveniences that come with it, but they can also be considered as works of art that display their owners' tastes. Anything can spark a new wave of consumer demand: for example, a newly launched art exhibition might trigger public interest in a particular color, or a bespoke pattern might appeal to young consumers who associate it with a sense of belonging.\n\nEager for creative encounters and outlets, the younger generation may look to automotive designs that enable them to discover, shape and express their individual identities in a meaningful way. In the near future, automotive designs that incorporate a mixed material palette and thoughtful aesthetics could cater to this need.",
        tags: ["AUTOMOTIVE", "AWAKENING", "MAKROLON®"],
        cmfDescription:
          "Using a single shade of red, experiment with varying levels of transparency and expressions of color to demonstrate the diverse range of visual design possibilities that can be achieved across different platforms. Makrolon® Ai2497, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior. Makrolon® Ai2457, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior. Contrasting hues of yellow and blue illustrate the ambivalent attitudes of the younger generation – the pearlescent blue represents new ideas generated by internal contradiction, while the translucid yellow embodies an emerging lucidity and clarity of thought. Makrolon® Ai2457, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior.",
        subsections: [
          {
            title: "Self-expression Reigns Supreme",
            text: "Social and recreational events, such as commercial art exhibitions, are leading the way in urban lifestyle trends. Young consumers enjoy outdoor activities such as skiing and fishing which were traditionally associated with the aristocracy, while also freely expressing themselves on social media.",
          },
          {
            title: "The Irreplicable 'Me'",
            text: "Automotive designs are developed with an eye on the bold and divergent purchasing patterns of young consumers around the globe. With a strong affinity for art and design, this generation is inclined towards palettes that emphasize their individuality and autonomy. They eschew rigid design principles in favor of pursuing rituals and a quality of life that brings them joy.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-meaningful-autonomy-chip1.png",
            samples: [
              {
                colorHex: "#C83030",
                name: "Tomato",
                ralColor: "RAL 030 50 40",
                material: "Makrolon® 2405",
                shape: "Solid, Metallic",
                appearance: "Matt Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=330004&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=330004&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#D04858",
                name: "Watermelon",
                ralColor: "RAL 030 60 40",
                material: "Makrolon® 2405",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=320013&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=320013&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-meaningful-autonomy-chip2.png",
            samples: [
              {
                colorHex: "#6090D0",
                name: "Frost",
                ralColor: "RAL 250 70 30",
                material: "Makrolon® AI2497",
                shape: "Translucent, Pearl",
                appearance: "Gloss Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=530010&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=530010&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#D8A020",
                name: "Mango",
                ralColor: "RAL 060 70 70",
                material: "Makrolon® 2405",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=120002&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=120002&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "the-next-destination": {
        title: "The Next Destination",
        collection: "Awakening · Automotive",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-lightweight-resilience.png",
        description:
          "Since its genesis, the automobile has undergone significant change in terms of exterior structure, functionality, and even aesthetic direction. Will the next wave of material and structural innovations pave the way for the next phase of automotive evolution? Current exploration of alternative materials and clean energy seems to indicate that cars will still have a place in future sustainable habitats.\n\nWith flexibility, cleanliness and safety in mind, the goal is to build a lightweight, energy-efficient solution — so that each time the engine runs, it casts less of a shadow on the environment.",
        tags: ["AUTOMOTIVE", "AWAKENING", "MAKROLON®"],
        cmfDescription:
          "Shades of black and purple evoke a sense of spaciousness, yet also reassuring reliability. Different levels of color opacity create a more effervescent visual appearance. The presence of rigid yet lightweight carbon composite further enhances this effect. Makrolon® Ai2417, crystal clear / transparent colored and tailored for parts with the highest requirements for purity and high gloss surfaces in automotive interior. Makrolon® Ai2457, tailored for translucent parts with the highest requirements for purity and even appearance in automotive and truck interior. Solid, earthy colors are a reminder that we have limited resources on our planet, while utilizing post-consumer recycled materials can help to encourage the implementation of circular systems. Bayblend® T85X R25, (PC+ABS) Blend with 25% PC recyclate, good injection molding processing behaviour (easy flowing), available in dark colors only.",
        subsections: [
          {
            title: "Lightweight Resilience",
            text: "Advanced automotive designs are increasingly lightweight, with additional innovative features that lower energy consumption and emissions, thus reducing environmental harm. A light yet compact external shell requires even stronger and more durable materials to ensure a safe and comfortable driving experience.",
          },
          {
            title: "Sustainable Development",
            text: "The earth's resources are not infinite. Car manufacturers are now taking on more social responsibility by reducing energy consumption in product life cycles, transitioning to gas-electric hybrid or full-electric models, and exploring the use of sustainable materials.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-the-next-destination-chip1.png",
            samples: [
              {
                colorHex: "#1A1018",
                name: "Obsidian",
                ralColor: "RAL 000 10 00",
                material: "Makrolon® Ai2417",
                shape: "Transparent",
                appearance: "Gloss Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=950001&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=950001&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#504050",
                name: "Mineral Grey",
                ralColor: "RAL 300 30 10",
                material: "Makrolon® Ai2457",
                shape: "Translucent",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=420003&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=420003&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-the-next-destination-chip2.png",
            samples: [
              {
                colorHex: "#8B6840",
                name: "Tawny",
                ralColor: "RAL 070 50 20",
                material: "Bayblend® T85X R25",
                shape: "Solid",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=800084&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=800084&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#C8A860",
                name: "Golden Sand",
                ralColor: "RAL 080 70 30",
                material: "Bayblend® T85X R25",
                shape: "Solid",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=100049&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=100049&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      // ── Electronics & Electrical ────────────────────────────────────────────
      "superhuman-2-5g": {
        title: "Superhuman 2.5G",
        collection: "Coexistence · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-personalized-interaction.jpg",
        description:
          "Humans have always been complex creatures, imbued with a blend of rational and emotional sensibilities that compel them to continually seek self-improvement. From skin contact and limb mobility to brain function, our bodies comprise extremely sophisticated mechanisms that require consistent maintenance, conscious or otherwise. Through meticulous research, analysis and mimicry of human characteristics, advanced technologies have come close to being able to reproduce interactions such as touch, temperature sensing and neurological activity.\n\nWith time, such developments are expected to enhance quality of life, augmenting our natural abilities whilst also making experiences more enjoyable. Humankind's endless curiosity and readiness to embrace technology could open up countless possibilities. In our exploration of human-machine interaction, what unexpected discoveries might we make? What uncharted territories and unknown worlds might be revealed to us? With the rapid advent of microcomputers and neuroscience, could the superhuman abilities imagined in science fiction films – such as using neurological pulses to communicate without uttering a single word – become reality?",
        tags: ["ELECTRONICS & ELECTRICAL", "COEXISTENCE", "MAKROLON®"],
        cmfDescription:
          "Bold, highly saturated colors in cool shades are evocative of cyberspace aesthetics. Transparent textures create a sense of breathability, which, when paired with technical surface finishes, act as a soft-shelled membrane, enhancing a user's haptic ability and physiological performance. The material has excellent optical clarity and allows for a high level of design freedom. Availability of flame-, UV- and chemical-resistance, make it suitable for use in personal and portable electronic devices. Metallic electric blues in varying levels of saturation create a fluid sensation of being in an electronic environment that is neither entirely physical, nor virtual. Matte and finely textured finishes help achieve greater visual depth, furthering the illusion of being between worlds. The material has a high gloss finish and can be processed easily. Thin wall flame retardant and UV stabilized, it features strong color stability even after long-term outdoor exposure, or in indoor settings exposed to sunlight.",
        subsections: [
          {
            title: "Personalized Interaction",
            text: "As the spectrum of human-machine interaction expands, wearables are no longer limited to one's wrist (watches, wristbands), feet (shoes, socks) or head (glasses, helmets, headbands), but will become comprehensive, customizable solutions that cater to human needs in terms of material, volume, comfort and function.",
          },
          {
            title: "Extended Functionality",
            text: "Whether through sensorial or intelligence-based means, humans have been augmenting themselves in different ways to extend the limitations of their bodies. A solution can be found in multisensory design, to push the boundaries of human perception.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-superhuman-25g-chip1.png",
            samples: [
              {
                colorHex: "#60B8A0",
                name: "Aurora Green",
                ralColor: "RAL 200 60 30",
                material: "Makrolon® 2405",
                shape: "Transparent",
                appearance: "Matt Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=650027&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=650027&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#C86090",
                name: "Particle Pink",
                ralColor: "RAL 340 50 35",
                material: "Makrolon® 6557",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=320012&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=320012&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-superhuman-25g-chip2.png",
            samples: [
              {
                colorHex: "#4050A0",
                name: "Super Power",
                ralColor: "RAL 260 40 40",
                material: "Bayblend® FR3005 HF",
                shape: "Solid, Metallic",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=530012&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=530012&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#A0B0D0",
                name: "Light Speed",
                ralColor: "RAL 270 70 15",
                material: "Bayblend® FR3016 W",
                shape: "Solid, Pearl",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=530013&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=530013&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "a-multifaceted-existence": {
        title: "A Multifaceted Existence",
        collection: "Coexistence · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-fragmented-lifestyles.png",
        description:
          'With the aggressive expansion of urban environments and exponential increase in global population, our natural resources are in decline. To combat this, we need smarter products in everyday life that can extend what is available and accommodate more possibilities.\n\nThis is where micro living spaces could be an effective solution. Le Cabanon, the vacation home of Le Corbusier, spans 14 square meters. It was the only place this founding father of modern architecture ever built for himself. Indeed, many contemporary designs are conceptualized with the aim of optimizing every inch of space, with built-in flexibility to be adapted in a fluid and agile manner. Smart interiors and concealed furniture, are some of the ways in which multitasking within a limited area can be made possible.\n\nThe multiple requirements of work, family, and other responsibilities tax our time to the limit, demanding that we constantly switch from one mode to another. One moment we are an efficient employee, then next a loving family member, whilst also balancing an active social life and making meaningful contributions to society. It can be exhausting.\n\nHumans long for comfort, a means of sheltering from external dangers and uncertainties. This plays out in our desire for living spaces that are soothing, clean, and spacious, but also provide entertainment, where we can find relief and validation.\n\nSmart appliances can be an at-home companion and co-manager of responsibilities, helping to boost a user\'s confidence and ease their transitions. Consider being able to ask your mirror "How do I look today?" and getting the response "You look great! Healthy, well-rested and that is the perfect outfit choice!"\n\nIntelligent devices could also help to promote a more positive spatial experience. Future living scenarios, aided by Internet of Things (IoT), modular home decorations and future generations of appliances, could see interiors transformed in a single click. Imagine simply turning off your TV and it seamlessly spins round to reveal a cabinet displaying soothing artwork.',
        tags: ["ELECTRONICS & ELECTRICAL", "COEXISTENCE", "MAKROLON®"],
        cmfDescription:
          "Pristine black is unobtrusive yet versatile, paired with soft, neutral jute tones and matte finishes. The overall effect is evocative of shifting boundaries, to reflect what it might be like to move freely between virtual and physical realities. The material can have high levels of transparency, with good chemical resistance and impact strength. White and green tones can help to nurture a peaceful, relaxed, and joyful atmosphere. Fine surface textures enhance glossy finishes, resulting in color that is angle-dependent, where hues appear to shift depending on the viewer's perspective. The resulting effect is that of a soothing yet multifaceted and transitional experience.",
        subsections: [
          {
            title: "Fragmented Lifestyles",
            text: "In modern city life, each individual takes on multiple roles, each requiring a different aspect of oneself. Future designs will enable city dwellers to quickly adjust to different scenarios, such as the creation of a quiet, secluded immersive environment, allowing them to focus on work. Such concepts can help us establish clear boundaries and avoid unnecessary interruptions.",
          },
          {
            title: "Adaptive Flexibility",
            text: "Flexible, adaptable all-in-one products provide a variety of solutions for different contexts – freeing up space and removing the need to purchase multiple items. Their features are designed to create a sense of fluidity, bridging the gap between different contexts through textural qualities, tactile contrast, and color effects. These products have distinctive visual and haptic qualities but can also be folded away and hidden.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-a-multifaceted-existence-chip1.png",
            samples: [
              {
                colorHex: "#1A1A1A",
                name: "Calm Black",
                ralColor: "RAL 790-3",
                material: "Makrolon® 2805",
                shape: "Solid",
                appearance: "Matt Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=900169&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=900169&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#C8B898",
                name: "Gauze Curtain",
                ralColor: "RAL 070 70 10",
                material: "Makrolon® GF9024",
                shape: "Translucent",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=820001&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=820001&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-a-multifaceted-existence-chip2.png",
            samples: [
              {
                colorHex: "#8B7840",
                name: "Fine Gold",
                ralColor: "RAL 085 40 20",
                material: "Makrolon® 205R50",
                shape: "Solid, Metallic",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?preliminaryColorCode=21G707M&colorSystem=preliminary_color_code",
                link: imagioLink(
                  "/search?preliminaryColorCode=21G707M&colorSystem=preliminary_color_code",
                ),
              },
              {
                colorHex: "#D0E8D0",
                name: "Halo",
                ralColor: "RAL 120 93 05",
                material: "Makrolon® 2405",
                shape: "Solid, Pearl, Color Travel",
                appearance: "Matt Back",
                searchUrl:
                  "/search?preliminaryColorCode=21G705M&colorSystem=preliminary_color_code",
                link: imagioLink(
                  "/search?preliminaryColorCode=21G705M&colorSystem=preliminary_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "transparent-society": {
        title: "Transparent Society",
        collection: "Coexistence · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-privacy-filters.png",
        description:
          "Information technology has transformed our world into a vast database. Under its gaze, everything is transparent. Our eating and drinking habits, interests and hobbies can be tracked and discovered in swathes of code. What will big data reveal about you?\n\nIt is highly likely that the smooth operation of megacities and future development of civilized societies will rely heavily on collecting and organizing data via public service platforms. During critical moments such as pandemics and disasters, drawing on the data provided by electronic products, while large databases can help reduce human exposure to potential risks.\n\nYet worries over privacy remain a concern – our faces being identified by monitoring devices without consent, contact details shared with third parties of unknown origin. How do we manage these concerns? While we celebrate news of progress and greater public transparency, can we truly hope to explore this ocean of data safely?",
        tags: ["ELECTRONICS & ELECTRICAL", "COEXISTENCE", "MAKROLON®"],
        cmfDescription:
          "Soft grey and purple in soothing shades, their calming effect magnified by transparent and translucent finishes, are paired with bubble-like surface textures that provide a sense of breathability, bringing comfort and stress relief. The grey material is highly transparent, with UV-, flame- and chemical-resistance, purple is thin-wall FR material with good mechanical properties. Black evokes a sense of calmness and hidden privacy, while carbon fiber composites project a solid, sturdy presence that is reassuring, making users feel secure and protected. Deep purple and ochre brown mirror the warning colors that some animals display in nature. The application of bespoke surface textures and color combinations or patterns, can also arouse a user's curiosity and enrich their haptic experience. The material has excellent transparency and allows for infrared penetration, rendering it ideal for security devices which need to be concealed.",
        subsections: [
          {
            title: "Privacy Filters",
            text: "With increased focus on intelligent solutions, products and services that make use of personal data will become more commonplace. The sharing of unique physiological data (fingerprints, voices, iris patterns and facial features) as well as individual details (names, occupations, etc.) poses further risk for violation of privacy. New user experiences should provide greater control over personal data and security – for instance, options for filtering of information and third-party supervision so users can decide if their details should be released, and if so, to what degree and for how long? Ultimately, we need to return decision-making power to the user, so they can enjoy the conveniences of an intelligent environment.",
          },
          {
            title: "Robust Protection",
            text: "In the era of social media, users move freely across different platforms and settings, selectively altering or sharing information as they go, to match the personas they wish to reflect. Will future designs take ethical considerations into account? When we are immersed in virtual experiences and other parallel worlds, experiencing satisfaction, recognition, courage or comfort, design should also highlight an important principle: just as we must bear the consequences of our actions in the real world, this also applies in virtual reality. Design and ethics should work in tandem to keep us alert to when our reality is being impacted by virtual movements.",
          },
          {
            title: "Public Surveillance",
            text: "How should digital devices be displayed in public areas? Should they be completely concealed without alerting humans to their existence, or be highlighted in the most conspicuous manner? These products can serve as a deterrent, but it is also important for people to feel that they are not under constant surveillance.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-transparent-society-chip1.png",
            samples: [
              {
                colorHex: "#A0A0A8",
                name: "Foggy Grey",
                ralColor: "RAL 000 70 00",
                material: "Makrolon® 6557",
                shape: "Translucid",
                appearance: "Gloss Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=720017&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=720017&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#9080B8",
                name: "Lavender",
                ralColor: "RAL 280 60 20",
                material: "Makrolon® LED5902FR",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=420004&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=420004&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-transparent-society-chip2.png",
            samples: [
              {
                colorHex: "#5A3A7A",
                name: "Purple Dragonfly",
                ralColor: "RAL 5022",
                material: "Makrolon® 205R50",
                shape: "Translucid",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=420002&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=420002&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#C87040",
                name: "Amber",
                ralColor: "RAL 8004",
                material: "Makrolon® 2407",
                shape: "Transparent",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=850009&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=850009&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-transparent-society-chip3.png",
            samples: [
              {
                colorHex: "#1A1A1A",
                name: "Shield",
                ralColor: "-",
                material: "Maezio®",
                shape: "Solid",
                appearance: "-",
              },
              {
                colorHex: "#2A2A2A",
                name: "Reborn",
                ralColor: "-",
                material: "Maezio®",
                shape: "Solid",
                appearance: "-",
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "conscious-consumption": {
        title: "Conscious Consumption",
        collection: "Coexistence · Electronics & Electrical",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-purposeful-living.png",
        description:
          "One of the main points of 2022 and beyond will be to conserve the earth's resources and avoid excessive consumption. In response to ecological issues, materials such as recyclable plastics and biodegradables are being developed at an accelerated pace, while clean, green sources of energy are also being deployed. Humans are beginning to view industrial products made from recycled materials as desirable, not just necessary.\n\nAs part of this global movement, we need to reconsider what a product lifecycle means. Is it a single, seamless entity for one singular use, or does it consist of many different components? In the manufacture and assembly of products, could the shared ownership of goods and re-use of resources be a better solution?\n\nThis could also mean rethinking our relationship with the products we already own – materials that have undergone long-term use achieve a worn aesthetic that has its own timeless appeal, even taking on a completely different patina over time. In retaining robust parts, while replacing those that have deteriorated, much like beloved childhood toys, products can be usable and sustainable while retaining sentimental value.",
        tags: ["ELECTRONICS & ELECTRICAL", "COEXISTENCE", "MAKROLON®"],
        cmfDescription:
          "Colors associated with nature and craft can elicit restorative sensations of being comforted and cared for. Soft, pastel yellow brings forth a sense of inner peace and security, cork-like textures exemplify the simple elegance of natural materials. Matte finishes offer a handcrafted appearance, building on the concept of low energy consumption. The material has good impact strength, UV stabilized and flame retardant. Utilizing materials that are more environmentally friendly, with comparable performance properties to virgin polycarbonate, can help clients to realize greener products with reduced carbon emissions. Blues and greens denote restraint and calm, while also symbolizing elements of the natural world such as mountains, streams, and forests. With a focus on more environmental friendliness, the material is made partly from recycled polycarbonate sheet and has good flame-resistance. Its lustrous pearlescent finish is in deliberate contrast with its humble origins, emphasizing its sophisticated transformation, to inspire further aesthetic explorations of regeneration. White and black form the foundation for this palette – evoking the romance of 'slow living', a restorative nurturing that is leisurely and peaceful. Matte finishes are reminiscent of organic textures, further building on the user's emotional connection with nature.",
        subsections: [
          {
            title: "Crafted with Care",
            text: "A rustic, organic aesthetic brings a refreshing warmth to standardized industrial designs, a reminder that pursuing scientific or technical improvement does not mean neglecting emotional, sensory qualities. Natural and handmade elements can act as distinguishing features placing a demand for refined details and sophisticated materials reminiscent of limited or small-batch production.",
          },
          {
            title: "Modular Regeneration",
            text: "It is possible to lengthen the shelf-life of products by enabling components to be replaced, upgraded and reconfigured, integrating green values with practical and aesthetic concerns. A variety of materials and textures can also be applied to enrich the narrative around the product. Such actions of reuse and renewal also allow owners to move away from throwaway culture and reconnect with their belongings in a more meaningful manner.",
          },
          {
            title: "Purposeful Living",
            text: "Humans are reacknowledging the power of nature, viewing it as a place to restore the balance of their physical and mental health. Environmentalism is not just a fashionable cause, but consumer behavior that stems from clear and rational analysis, and reflection of human impact on the environment. Solutions are not to be found in extremes – slow living does not equate to blindly following traditional lifestyles; high-speed, high-pressure and high-density environments are not the answer either. Instead, humans can be prudent in accommodating diverse attitudes and making instinctive adjustments to their pace of life.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-conscious-consumption-chip1.png",
            samples: [
              {
                colorHex: "#6B4020",
                name: "Cork",
                ralColor: "RAL 060 30 30",
                material: "Makrolon® FR6017R30",
                shape: "Solid, Marble",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?preliminaryColorCode=21C603M&colorSystem=preliminary_color_code",
                link: imagioLink(
                  "/search?preliminaryColorCode=21C603M&colorSystem=preliminary_color_code",
                ),
              },
              {
                colorHex: "#E8D870",
                name: "Sunshine",
                ralColor: "RAL 085 90 30",
                material: "Makrolon® FR6020RE",
                shape: "Solid",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=100053&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=100053&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-conscious-consumption-chip2.png",
            samples: [
              {
                colorHex: "#50B8B8",
                name: "Fresh Blue",
                ralColor: "RAL 690-2",
                material: "Bayblend® FR630",
                shape: "Solid, Pearl",
                appearance: "Gloss Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=530011&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=530011&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#405858",
                name: "Mountain Green",
                ralColor: "RAL 160 50 10",
                material: "Bayblend® FR3020 W BBS910",
                shape: "Solid",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=600098&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=600098&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
          {
            image: "/imgs/trend/cmf20223-conscious-consumption-chip3.png",
            samples: [
              {
                colorHex: "#E8E4D8",
                name: "Pebble",
                ralColor: "RAL 9002",
                material: "Bayblend® FR3010 R75",
                shape: "Solid",
                appearance: "Matt Front / Matt Back",
                searchUrl:
                  "/search?finalColorCode=700620&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=700620&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#808078",
                name: "Rock",
                ralColor: "RAL 870-5",
                material: "Bayblend® FR3008 R65",
                shape: "Solid, Marble",
                appearance: "Matt Back",
                searchUrl:
                  "/search?preliminaryColorCode=21C471B&colorSystem=preliminary_color_code",
                link: imagioLink(
                  "/search?preliminaryColorCode=21C471B&colorSystem=preliminary_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      // ── Healthcare ──────────────────────────────────────────────────────────
      "intelligent-interaction": {
        title: "Intelligent Interaction",
        collection: "Empowerment · Healthcare",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-discreet-biomimicry.png",
        description:
          "With the development of artificial intelligence (AI), healthcare and wellness devices have created new possibilities for maintaining daily health. AI powered devices can provide daily at-home medical services, identifying symptoms and other medical information through patient interaction, providing medical professionals with more accurate data, making the diagnosis process easier and more efficient. These intelligent designs not only interpret and manage symptoms, but can also provide a level of emotional comfort for a more pleasant treatment experience.\n\nThese health and wellness innovations are often propelled by smart sensor technologies, where human to machine interaction becomes a two way dialogue. As machines learn to detect and interpret human behavior through signals such as facial expressions and body language, devices can now also provide a sense of emotional wellness and reassurance in response. In medical and healthcare devices, advanced sensory systems such as precise motion and tactile recognition can now supplement human connection, when access to a provider may be inaccessible.\n\nWhile the ethical implication of AI devices must be taken into consideration, scenarios like these show how intelligent solutions are suitable for a wide range of applications including formal diagnosis, treatment, and rehabilitation – beyond the boundaries of traditional medicine and healthcare.",
        tags: ["HEALTHCARE", "EMPOWERMENT", "MAKROBLEND®", "MAKROLON®"],
        cmfDescription:
          "Unsaturated yet bright hues provide a natural, warm feeling, an ideal foundation for friendly interaction between user and interface. Matt and translucent qualities exude a comforting aura and draw the user in, tempering the effect of strong technical textures for a more restrained aesthetic. Skin biocompatible (polycarbonate+polyester)-blend Makroblend® M525 is chemically resistant and safe for contact with uncompromised skin, tested according to ISO 10993-5 in-vitro cytotoxicity as well as ISO 10993-10 irritation and skin sensitization. Makrolon® 2458 is medical grade polycarbonate that has ISO 10993-1 certification and is suitable for ethylene oxide or steam sterilization. It also has low viscosity and can be easily demolded.",
        subsections: [
          {
            title: "Discreet Biomimicry",
            text: "Advanced voice recognition technology, wearable devices and designs which can interpret neurological signals are enabling those who are neurodiverse to enjoy new experiences with the help of multi-sensory support and feedback. User experiences of medical devices have been vastly improved, providing feelings of warmth and comfort. Yet it is also essential to moderate the level of realism for biomimetic properties, to avoid the effects of the 'Uncanny Valley' – a term coined by Masahiro Mori, where designs are almost lifelike and cause feelings of unease.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-intelligent-interaction-chip1.png",
            samples: [
              {
                colorHex: "#F0E0E0",
                name: "Kitten's Footprint",
                ralColor: "RAL 340 93 05",
                material: "Makroblend® M525",
                shape: "Solid",
                appearance: "Matt Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=300125&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=300125&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#F0D0C0",
                name: "Milk Candy",
                ralColor: "RAL 075 80 10",
                material: "Makrolon® 2458",
                shape: "Translucent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=820003&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=820003&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "inclusive-well-being": {
        title: "Inclusive Well-being",
        collection: "Empowerment · Healthcare",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-connected-communities.png",
        description:
          "As society continues to evolve and change, the daily structure of work and homelife has shifted family structures and responsibilities. Whether it's a declining population or falling birth rates, the way we live is being restructured by a host of societal factors. With an aging population, there is a growing need for devices which can bridge the gap between the experiences of the young and old. Even our perception of \"old\" is outdated – the 'Young Old' are thriving, and many older people would rather indulge in the present than save for a rainy day.\n\nMultiple demographic shifts are generating opportunities for growth and progress: young consumers choosing to live alone, older individuals wanting to indulge in the present, and others placing greater value on self-improvement.",
        tags: ["HEALTHCARE", "EMPOWERMENT", "MAKROLON®"],
        cmfDescription:
          "Strong blue provides a strong vital force. Warm, soft shades of pink provide a reassuring yet unobtrusive sense of security, while pearlescent finishes have a spirited appeal. Makrolon® 2858 is medical-grade polycarbonate that is biocompatible according to relevant ISO 10993-1 test requirements and is suitable for ethylene oxide or steam sterilization. It also has medium viscosity and good impact resistance. Makrolon® 6557 is a UV-stable polycarbonate that is flame-resistant, with a UL94 V-0 rating at a thickness of 3mm. It has medium viscosity and can be demolded easily.",
        subsections: [
          {
            title: "Connected Communities",
            text: "Multiple demographic shifts are generating opportunities for growth and progress: young consumers choosing to live alone, older individuals wanting to indulge in the present, and others placing greater value on self-improvement. Vivid designs reflect the modernity of urban life, while vibrant colors serve as a reminder to stay connected to one another. Authentic yet adaptable interpersonal interactions could pave the way for more positive societal development.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-inclusive-well-being-chip1.png",
            samples: [
              {
                colorHex: "#3A3A6A",
                name: "Purple Blue",
                ralColor: "RAL 290 30 25",
                material: "Makrolon® 2858",
                shape: "Solid",
                appearance: "Matt Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=400019&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=400019&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#C870A0",
                name: "Lustrous Pink",
                ralColor: "RAL 010 70 35",
                material: "Makrolon® 6557",
                shape: "Transparent, Pearl",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=330005&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=330005&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "empathy-amplified": {
        title: "Empathy Amplified",
        collection: "Empowerment · Healthcare",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-sensorial-clarity.png",
        description:
          "Many individuals seek to maintain their physical health, but youthful health and energy is difficult to sustain as one grows older. Now with modern medicine, current concerns are not necessarily about longevity, but instead focus on the quality and dignity of life as people age. Technology and devices offer solutions to mitigate some of the physiological deterioration which usually occurs later in life.\n\nIt is important to also reconsider the concept of daily care to ensure a variety of solutions are available so users with impairments or disabilities can equally benefit from the convenience of modern living. This not only has positive benefits for the individual, but a positive societal impact as well.\n\nThere is an increasingly empathetic perspective toward individuals with varying abilities and those who are neurodiverse. Voice recognition, sensing and exo-skeletal technologies are making advances to alleviate gradual or partial disabilities. These advances will move society closer to a future where ability level and aging will no longer be as burdensome.",
        tags: ["HEALTHCARE", "EMPOWERMENT", "BAYBLEND®", "MAKROLON®"],
        cmfDescription:
          "Bright, vivid high-intensity colors in strong contrasting combinations can serve as warning signs or provide vulnerable users with greater confidence. High gloss and glowing edge effects can further emphasize the visibility of this effect. (PC+ABS)- blend Bayblend® M850 XF is easy flowing and biocompatible, meeting relevant requirements of ISO Standard 10993-1. Makrolon® 2805 is available for general purpose applications. It has medium viscosity and can also be demolded easily.",
        subsections: [
          {
            title: "Sensorial Clarity",
            text: "Future designs will also be a means of visual communication, using materials and colors to strengthen visible cues for users with impairment, for improved recognition, safety, and usability.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-empathy-amplified-chip1.png",
            samples: [
              {
                colorHex: "#4AB89A",
                name: "Apple Green",
                ralColor: "RAL 180 60 45",
                material: "Bayblend® M850 XF",
                shape: "Solid",
                appearance: "Matt Front / Gloss Back",
                searchUrl:
                  "/search?finalColorCode=600105&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=600105&colorSystem=covestro_color_code",
                ),
              },
              {
                colorHex: "#F06020",
                name: "Orange Glow",
                ralColor: "RAL 050 60 80",
                material: "Makrolon® 2805",
                shape: "Transparent, Fluorescent",
                appearance: "Gloss Back",
                searchUrl:
                  "/search?finalColorCode=250006&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=250006&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
      "resilient-optimism": {
        title: "Resilient Optimism",
        collection: "Empowerment · Healthcare",
        collectionSlug: "cmf-design-trends-2022-2023",
        heroImage: "/imgs/trend/ace-smart-sustainability.png",
        description:
          "Recent events such as unprecedented wildfires, hurricanes, winter storms and heatwaves have renewed our attention on environmental conditions. These events are awakening us to examine and modify our behaviors otherwise our actions may cause unintended consequences.\n\nResearch is underway for how society can best live with these new challenges and the conditions they pose. Strategies for health and wellness in extreme environments, as well as protective measures for individual safety or environmental adaptability are likely to become a key focus of future healthcare products. At present, drones are being developed to pinpoint disaster areas and support relief rescue aid. Miniature and portable versions of traditionally large-scale medical devices are being created so treatment and assistance can be administered in remote, underserved, or disaster-stricken areas.\n\nFrom reusable materials and clean energy sources, to recycling systems which reduce pressure on the environment, people are looking for solutions to reduce and reverse environmental challenges.",
        tags: ["HEALTHCARE", "EMPOWERMENT", "MAKROLON®"],
        cmfDescription:
          "Muted hues provide a strong sense of reliability and security, while marbled patterns and sandy, matte textures reminiscent of earth and minerals celebrate the vitality of wild, natural landscapes. Makrolon® FR6011 is a halogen-free, flame-resistant polycarbonate with a UL94 V-0 rating at a thickness of 1.5mm. It is suitable for contact with household chemical and cleaning solvents, with medium viscosity as well as UV-stability and impact-resistance. New product medical Makrolon® M6011 FR is having next generation of flame retardancy with a UL94 V-0 rating at a thickness of 1.5mm and good dimensional stability. It is resistant to both daily chemicals and disinfectants, with medium viscosity, UV-stability and impact resistance. It is safe for contact with uncompromised skin, tested according to ISO 10993-5 in-vitro cytotoxicity as well as ISO 10993-10 irritation and skin sensitization. Makrolon® 2458 is a medical-grade polycarbonate that fulfills ISO 10993-1 requirements. It is suitable for ethylene oxide or steam sterilization, has low viscosity and can easily be demolded. Carbon composite material is lightweight and offers exceptional rigidity.",
        subsections: [
          {
            title: "Protective Tactility",
            text: "New medical devices and health examination facilities can perform in hostile environments, alerting users to danger and actively saving lives. Compared to finer, smoother surface textures, a coarser finish can stimulate a user's haptic reflexes further, strengthening their sense of recognition and security.",
          },
          {
            title: "Smart Sustainability",
            text: "Advancements in technology are expediting the implementation of sustainable design principles in medical devices. In contexts which require the utmost safety, flexibility in assembly and disassembly of components helps to ensure parts are reused or recycled.",
          },
        ],
        materialGroups: [
          {
            image: "/imgs/trend/cmf20223-resilient-optimism-chip2.png",
            samples: [
              {
                colorHex: "#8B4030",
                name: "Mars",
                ralColor: "RAL 030 40 40",
                material: "Makrolon® FR6011",
                shape: "Solid, Marble",
                appearance: "Matt Front / Matt Back",
                searchUrl:
                  "/search?preliminaryColorCode=20C497M&colorSystem=preliminary_color_code",
                link: imagioLink(
                  "/search?preliminaryColorCode=20C497M&colorSystem=preliminary_color_code",
                ),
              },
              {
                colorHex: "#8898A0",
                name: "Stone",
                ralColor: "RAL 7001",
                material: "Makrolon® 2458",
                shape: "Solid",
                appearance: "Matt Back",
                searchUrl:
                  "/search?finalColorCode=700625&colorSystem=covestro_color_code",
                link: imagioLink(
                  "/search?finalColorCode=700625&colorSystem=covestro_color_code",
                ),
              },
            ],
          },
        ],
        materialSamples: [],
      },
    },
  },
  // ── Aesthetics of Sustainability ───────────────────────────────────────────────
  "aesthetics-of-sustainability": {
    slug: "aesthetics-of-sustainability",
    title: "Aesthetics of Sustainability",
    heroImage: "/imgs/trend/aor-hero.png",
    heroChip: "Covestro × Tsinghua University",
    heroExploreLabel: "Explore Guide",
    // Figma hero has no body copy under the title — suppress the introText[0] fallback.
    heroDescription: "",
    introTitle: "Unique Collaboration",
    themes: [],
    introText: [
      "Covestro has collaborated with the prestigious Colour & Imaging Institute of the Arts & Science Research Centre at Tsinghua University to explore the CMF aesthetics of more sustainable plastics.",
      "This collection of colors and effects, based on Covestro’s recycled and renewable attributed polycarbonates, has been delivered through consumer surveys, expert interviews, big data mining, and design industry research in China.",
      "Covestro and the Colour & Imaging Institute of Tsinghua University are committed to creating an aesthetic design scheme for the future with materials based on their sustainability attributes. The collaboration aims to accelerate the market adoption of more sustainable plastics by striking a delicate balance between aesthetics, performance, and sustainability.",
    ],
    quote:
      "«In ancient Asia, people followed the principle of “harmony between man and nature”. In other words, people followed the laws of nature, got along with nature, and made compromises in life. Therefore, people in Asia have had the concept of “circularity” since ancient times.» — Song Limin | Director of the Institute of Urban Landscape Design, Tsinghua University",
    sections: [
      {
        title: "Five Themes",
        subtitle: "Inspired by nature",
        cards: [
          {
            slug: "earth",
            title: "Earth",
            image: "/imgs/trend/aor-earth.png",
            description:
              "Asian and western countries have different perspectives on nature and the concept of sustainability. In daily life, Asian people have embraced more refined and sophisticated designs for products that incorporate sustainable concepts.",
            colors: [
              { hex: "#E6DDCE", name: "Sand White" },
              { hex: "#C4A07A", name: "Burly Wood Khaki" },
              { hex: "#B0A89E", name: "Pebble Grey" },
              { hex: "#BBB459", name: "Moss Green" },
              { hex: "#D8A090", name: "Rock Pink" },
            ],
          },
          {
            slug: "water",
            title: "Water",
            image: "/imgs/trend/aor-water.png",
            description:
              "Far back in ancient times, Emperor Shun established the oldest environmental protection management organization in the world. In ancient China, environmental protection legislation was usually presented in the form of laws, imperial edicts, rites, prohibitions, etc.",
            colors: [
              { hex: "#6B5040", name: "Smoky Quartz" },
              { hex: "#4A8C6A", name: "Green Lake" },
              { hex: "#2A6A8A", name: "Blue Coral" },
              { hex: "#A0C8D8", name: "H₂O" },
              { hex: "#4080A0", name: "Lake Blue" },
            ],
          },
          {
            slug: "gems",
            title: "Gems",
            image: "/imgs/trend/aor-gems.png",
            description:
              "To truly move people at the heart, it is necessary to inform consumers from the most direct visual level that more sustainable materials like recycled plastics are expensive and exquisite.",
            colors: [
              { hex: "#6B6FA8", name: "Lapis Lazuli" },
              { hex: "#40C8C8", name: "Turquoise" },
              { hex: "#3D7A5A", name: "Jadeite" },
              { hex: "#E8A020", name: "Amber" },
              { hex: "#8B3A4A", name: "Tourmaline" },
            ],
          },
          {
            slug: "stone",
            title: "Stone",
            image: "/imgs/trend/aor-stone.png",
            description:
              "In the future, people will even accept defective products that are not dyed, unsaturated, nostalgic, and close to people.",
            colors: [
              { hex: "#D8D4CE", name: "Calcite White" },
              { hex: "#4A5538", name: "Sea Kelp" },
              { hex: "#908E88", name: "Lavender Grey" },
              { hex: "#BCC4A0", name: "Dawn Grey" },
              { hex: "#C4D8DC", name: "Winter Sky" },
            ],
          },
          {
            slug: "black-white",
            title: "Black & White",
            image: "/imgs/trend/aor-black-white.png",
            description:
              "Circular materials are described as sturdy and durable.",
            colors: [
              { hex: "#FFFFFF", name: "White" },
              { hex: "#1A1A1A", name: "Black" },
            ],
          },
        ],
      },
    ],
    cardDetails: {
      earth: {
        title: "Earth",
        collection: "Aesthetics of Sustainability",
        collectionSlug: "aesthetics-of-sustainability",
        heroImage: "/imgs/trend/aor-earth.png",
        heroQuote:
          "Asian and western countries have different perspectives on nature and the concept of sustainability. In daily life, Asian people have embraced more refined and sophisticated designs for products that incorporate sustainable concepts.",
        heroAttribution:
          "— Song Limin, Director of the Institute of Urban Landscape Design, Tsinghua University",
        heroSwatches: [
          { hex: "#E6DDCE", name: "Sand White" },
          { hex: "#C4A07A", name: "Burly Wood Khaki" },
          { hex: "#B0A89E", name: "Pebble Grey" },
          { hex: "#BBB459", name: "Moss Green" },
          { hex: "#D8A090", name: "Rock Pink" },
        ],
        description:
          "The Earth holds everything, nurtures everything, and encompasses everything quietly and silently. The earthen tones with low-saturation and medium-high brightness come from nature, and are familiar and intimate, making us feel relaxed and safe. The Earth is as simple and beautiful as it was at the beginning of creation, reminding us of the intimate symbiosis between man and nature. The Earth is like the wilderness in a spring breeze, telling the stories of changing seasons, and of life and growth in nature. There are no words that can fully describe the beauty of the Earth, only sing of its wondrousness.",
        tags: [
          "EARTH TONES",
          "RECYCLED PC",
          "BIOPHILIC DESIGN",
          "SUSTAINABLE CMF",
        ],
        materialSamples: [
          {
            image: "/imgs/trend/aor-earth-sand-white.png",
            colorHex: "#E6DDCE",
            name: "Sand White",
            ralColor: "RAL 070 80 10",
            material: "Bayblend® FR3021 R5B",
            shape: "Solid",
            appearance: "Gloss",
            productId: "800098",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=800098&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=800098&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-earth-burly-wood-khaki.png",
            colorHex: "#C4A07A",
            name: "Burly Wood Khaki",
            ralColor: "RAL 060 70 20",
            material: "Bayblend® FR3010 R75",
            shape: "Solid",
            appearance: "Gloss",
            productId: "800116",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=800116&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=800116&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-earth-pebble-grey.png",
            colorHex: "#B0A89E",
            name: "Pebble Grey",
            ralColor: "RAL 000 80 00",
            material: "Bayblend® FR6017 R30",
            shape: "Solid",
            appearance: "Matt",
            productId: "700758",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=700758&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=700758&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-earth-moss-green.png",
            colorHex: "#BBB459",
            name: "Moss Green",
            ralColor: "RAL 120 70 20",
            material: "Bayblend® FR3010 R75",
            shape: "Solid",
            appearance: "Matt",
            productId: "600125",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=600125&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=600125&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-earth-rock-pink.png",
            colorHex: "#D8A090",
            name: "Rock Pink",
            ralColor: "RAL 030 70 20",
            material: "Bayblend® FR3008 R65",
            shape: "Solid",
            appearance: "Matt",
            productId: "300136",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=300136&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&hasDigitalSample=true&opacity=OPAQUE&finalColorCode=300136&colorSystem=covestro_color_code",
            ),
          },
        ],
      },
      water: {
        title: "Water",
        collection: "Aesthetics of Sustainability",
        collectionSlug: "aesthetics-of-sustainability",
        heroImage: "/imgs/trend/aor-water.png",
        heroQuote:
          "Far back in ancient times, Emperor Shun established the oldest environmental protection management organization in the world. In ancient China, environmental protection legislation was usually presented in the form of laws, imperial edicts, rites, prohibitions, etc. Most of them were released at the same time as other legal provisions, showing comprehensive characteristics.",
        heroAttribution: "— Wang Yige, Literary and Art Commentator & Curator",
        heroSwatches: [
          { hex: "#6B5040", name: "Smoky Quartz" },
          { hex: "#4A8C6A", name: "Green Lake" },
          { hex: "#2A6A8A", name: "Blue Coral" },
          { hex: "#A0C8D8", name: "H₂O" },
          { hex: "#4080A0", name: "Lake Blue" },
        ],
        description:
          "There are long rivers and vast seas, high gorges and flat lakes on the Earth. Water is the source of life and the cradle of civilization. Transparent/translucent texture plus the color of water ensure transparency and purity. The unobstructed view of the content reflects the truthfulness of circular materials taking examples from nature, and the sense of control brought to mankind by the development of high technology. The water color with high saturation is glittering and translucent, representing pleasure and freedom. With low saturation the water color is tender and introverted, representing broad mindedness and tolerance. Let us intertwine our feelings and let out feelings show with water.",
        tags: ["AQUA TONES", "DROP-IN SOLUTION", "LOW CARBON", "RECYCLED CQ"],
        materialSamples: [
          {
            image: "/imgs/trend/aor-water-smoky-quartz.png",
            colorHex: "#6B5040",
            name: "Smoky Quartz",
            ralColor: "RAL 070 40 40",
            material: "Makrolon® 2407 RE",
            shape: "Solid",
            appearance: "Gloss",
            productId: "850014",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=850014&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=850014&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-water-green-lake.png",
            colorHex: "#4A8C6A",
            name: "Green Lake",
            ralColor: "RAL 130 70 60",
            material: "Makrolon® 2407 RE",
            shape: "Solid",
            appearance: "Gloss",
            productId: "650038",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=650038&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=650038&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-water-blue-coral.png",
            colorHex: "#2A6A8A",
            name: "Blue Coral",
            ralColor: "RAL 230 30 20",
            material: "Makrolon® 2407 RE",
            shape: "Solid",
            appearance: "Gloss",
            productId: "550137",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550137&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550137&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-water-h2o.png",
            colorHex: "#A0C8D8",
            name: "H₂O",
            ralColor: "RAL 250 70 10",
            material: "Makrolon® 2407 RE",
            shape: "Solid",
            appearance: "Gloss",
            productId: "550138",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550138&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550138&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-water-lake-blue.png",
            colorHex: "#4080A0",
            name: "Lake Blue",
            ralColor: "RAL 240 60 35",
            material: "Makrolon® 2407 RE",
            shape: "Solid",
            appearance: "Gloss",
            productId: "550139",
            searchUrl:
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550139&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?colorTag=15%3ACMF-related&opacity=TRANSPARENT&finalColorCode=550139&colorSystem=covestro_color_code",
            ),
          },
        ],
      },
      gems: {
        title: "Gems",
        collection: "Aesthetics of Sustainability",
        collectionSlug: "aesthetics-of-sustainability",
        heroImage: "/imgs/trend/aor-gems.png",
        heroQuote:
          "To truly move people at the heart, it is necessary to inform consumers from the most direct visual level that more sustainable materials like recycled plastics are expensive and exquisite.",
        heroAttribution: "— Shi Xiaoxi, Mentor of Tsinghua’s x-lab",
        heroSwatches: [
          { hex: "#6B6FA8", name: "Lapis Lazuli" },
          { hex: "#40C8C8", name: "Turquoise" },
          { hex: "#3D7A5A", name: "Jadeite" },
          { hex: "#E8A020", name: "Amber" },
          { hex: "#8B3A4A", name: "Tourmaline" },
        ],
        description:
          "A gem is an aesthetic treasury of nature. Throughout history, mineral pigments have been a means of achieving the ultimate pure colors. The gorgeous color, crystalline texture and brilliant luster have always been the embodiment of luxury and quality. A gem, which is rich in color and luster yet retains a natural coarse texture, gives a delicate and magnificent charm to circular materials while preserving the natural atmosphere.",
        tags: [
          "JEWEL TONES",
          "HIGH GLOSS",
          "EFFECT PIGMENT",
          "PREMIUM RECYCLED",
        ],
        materialSamples: [
          {
            image: "/imgs/trend/aor-gems-lapis-lazuli.png",
            colorHex: "#6B6FA8",
            name: "Lapis Lazuli",
            ralColor: "RAL 290 60 25",
            material: "Makrolon® FR6017 R30",
            shape: "Solid",
            appearance: "Gloss",
            productId: "400044",
            searchUrl:
              "/search?finalColorCode=400044&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=400044&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-gems-turquoise.png",
            colorHex: "#40C8C8",
            name: "Turquoise",
            ralColor: "RAL 210 70 35",
            material: "Makrolon® 705 R50",
            shape: "Solid",
            appearance: "Gloss",
            productId: "500423",
            searchUrl:
              "/search?finalColorCode=500423&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=500423&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-gems-jadeite.png",
            colorHex: "#3D7A5A",
            name: "Jadeite",
            ralColor: "RAL 150 30 20",
            material: "Bayblend® FR3010 R75",
            shape: "Solid",
            appearance: "Matt",
            productId: "600124",
            searchUrl:
              "/search?finalColorCode=600124&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=600124&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-gems-amber.png",
            colorHex: "#E8A020",
            name: "Amber",
            ralColor: "RAL 080 70 80",
            material: "Makrolon® 2407 RE",
            shape: "Solid, Metallic",
            appearance: "Gloss",
            productId: "130004",
            searchUrl:
              "/search?finalColorCode=130004&colorSystem=covestro_color_code&opacity=OPAQUE&colorTag=15%3ACMF-related&hasDigitalSample=true&colorEffect=METALLIC_OR_PEARLESCENT",
            link: imagioLink(
              "/search?finalColorCode=130004&colorSystem=covestro_color_code&opacity=OPAQUE&colorTag=15%3ACMF-related&hasDigitalSample=true&colorEffect=METALLIC_OR_PEARLESCENT",
            ),
          },
          {
            image: "/imgs/trend/aor-gems-tourmaline.png",
            colorHex: "#8B3A4A",
            name: "Tourmaline",
            ralColor: "RAL 030 40 60",
            material: "Makrolon® FR6017 R30",
            shape: "Solid",
            appearance: "Metallic / Pearlescent, Fluorescent",
            productId: "330006",
            searchUrl:
              "/search?finalColorCode=330006&colorSystem=covestro_color_code&opacity=OPAQUE&colorTag=15%3ACMF-related&hasDigitalSample=true&colorEffect=METALLIC_OR_PEARLESCENT",
            link: imagioLink(
              "/search?finalColorCode=330006&colorSystem=covestro_color_code&opacity=OPAQUE&colorTag=15%3ACMF-related&hasDigitalSample=true&colorEffect=METALLIC_OR_PEARLESCENT",
            ),
          },
        ],
      },
      stone: {
        title: "Stone",
        collection: "Aesthetics of Sustainability",
        collectionSlug: "aesthetics-of-sustainability",
        heroImage: "/imgs/trend/aor-stone.png",
        heroQuote:
          "In the future, people will even accept defective products that are not dyed, unsaturated, nostalgic, and close to people.",
        heroAttribution:
          "— Yan Yang, Principal of Transportation Design Specialty, School of Arts and Design, Tsinghua University",
        heroSwatches: [
          { hex: "#D8D4CE", name: "Calcite White" },
          { hex: "#4A5538", name: "Sea Kelp" },
          { hex: "#908E88", name: "Lavender Grey" },
          { hex: "#BCC4A0", name: "Dawn Grey" },
          { hex: "#C4D8DC", name: "Winter Sky" },
        ],
        description:
          "Stone, thick and solid, is the most familiar natural material having the longest history. The essence of heaven and earth is condensed into stone, piercing the surface. A product of boiling lava under heavy pressure, stone is naturally endowed with a hard, calm and restrained texture. Soft and soothing colors, combined with mild and long-lasting temperament, give double appearance and multi-level textures to this series, highlighting the charm of recycled materials recreated in forging.",
        tags: [
          "MINERAL TONES",
          "RECYCLED PC",
          "TEXTURED SURFACE",
          "ECO-CONSCIOUS",
        ],
        materialSamples: [
          {
            image: "/imgs/trend/aor-stone-calcite-white.png",
            colorHex: "#D8D4CE",
            name: "Calcite White",
            ralColor: "RAL 320 90 05",
            material: "Bayblend® FR3008 R65",
            shape: "Solid",
            appearance: "Gloss",
            productId: "030009",
            searchUrl:
              "/search?finalColorCode=030009&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=030009&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-stone-sea-kelp.png",
            colorHex: "#4A5538",
            name: "Sea Kelp",
            ralColor: "RAL 110 40 20",
            material: "Bayblend® T85X R25",
            shape: "Solid",
            appearance: "Gloss",
            productId: "630005",
            searchUrl:
              "/search?finalColorCode=630005&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=630005&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-stone-lavender-grey.png",
            colorHex: "#908E88",
            name: "Lavender Grey",
            ralColor: "RAL 000 50 00",
            material: "Bayblend® FR3010 R75",
            shape: "Solid",
            appearance: "Matt",
            productId: "730038",
            searchUrl:
              "/search?finalColorCode=730038&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=730038&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-stone-dawn-grey.png",
            colorHex: "#BCC4A0",
            name: "Dawn Grey",
            ralColor: "RAL 130 80 10",
            material: "Bayblend® FR3070 R35 CN",
            shape: "Solid",
            appearance: "Gloss",
            productId: "630008",
            searchUrl:
              "/search?finalColorCode=630008&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=630008&colorSystem=covestro_color_code",
            ),
          },
          {
            image: "/imgs/trend/aor-stone-winter-sky.png",
            colorHex: "#C4D8DC",
            name: "Winter Sky",
            ralColor: "RAL 230 80 10",
            material: "Makrolon® 705 R50",
            shape: "Solid",
            appearance: "Gloss",
            productId: "530016",
            searchUrl:
              "/search?finalColorCode=530016&colorSystem=covestro_color_code",
            link: imagioLink(
              "/search?finalColorCode=530016&colorSystem=covestro_color_code",
            ),
          },
        ],
      },
      "black-white": {
        title: "Black & White",
        collection: "Aesthetics of Sustainability",
        collectionSlug: "aesthetics-of-sustainability",
        heroImage: "/imgs/trend/aor-black-white.png",
        heroQuote: "Circular materials are described as sturdy and durable.",
        heroAttribution:
          "— Zhou Haoming, Director of the Sustainable Design Research Institute, Art and Science Research Centre, Tsinghua University",
        heroSwatches: [
          { hex: "#FFFFFF", name: "White" },
          { hex: "#1A1A1A", name: "Black" },
        ],
        description:
          "Black and white are the first words for color appeared in human language, and they signify the most original and important cognition of natural colors by human beings. Sunrise is bright; it is, therefore, called white. Midnight is dark; it is, therefore, called black. White is the confluence of all colors, while black is the disintegration of all colors. The visual simplicity and cleanliness make them enduringly classic colors. Black and white are omnipresent in the design. Eternal alternation of day and night, and the everlasting black and white, perfectly match the sustainable nature of recycled materials.",
        tags: [
          "CIRCULAR MATERIAL",
          "RECYCLED PC",
          "EXTERIOR GRADE",
          "MATT FINISH",
        ],
        materialSamples: [
          {
            image: "/imgs/trend/aor-bw-white.png",
            colorHex: "#FFFFFF",
            name: "White",
            ralColor: "RAL 000 90 00",
            material: "Makrolon® FR6017 R50",
            shape: "Solid",
            appearance: "Matt",
            productId: "010374",
            searchUrl:
              "/search?finalColorCode=010374&colorSystem=covestro_color_code&grade=MAKROLON%C2%AE+FR6017+R50&grade=Makrolon%C2%AE+705+R50",
            link: imagioLink(
              "/search?finalColorCode=010374&colorSystem=covestro_color_code&grade=MAKROLON%C2%AE+FR6017+R50&grade=Makrolon%C2%AE+705+R50",
            ),
          },
          {
            image: "/imgs/trend/aor-bw-black.png",
            colorHex: "#1A1A1A",
            name: "Black",
            ralColor: "RAL 000 15 00",
            material: "Makrolon® 705 R50",
            shape: "Solid",
            appearance: "Matt",
            productId: "901510",
            searchUrl:
              "/search?finalColorCode=901510&colorSystem=covestro_color_code&grade=MAKROLON%C2%AE+FR6017+R50&grade=Makrolon%C2%AE+705+R50",
            link: imagioLink(
              "/search?finalColorCode=901510&colorSystem=covestro_color_code&grade=MAKROLON%C2%AE+FR6017+R50&grade=Makrolon%C2%AE+705+R50",
            ),
          },
        ],
      },
    },
  },
};

// ── Carousel items derived from trendData ─────────────────────────────────────
// Same TrendCarouselItem shape — useTrendCarouselItems can switch to API without
// changing its return contract.

const NAVIGABLE_SLUGS = new Set([
  "cmf-design-trends-2025",
  "aesthetics-of-sustainability",
  "cmf-design-trends-2022-2023",
]);

const CAROUSEL_ORDER = [
  "cmf-design-trends-2025",
  "aesthetics-of-sustainability",
  "cmf-design-trends-2022-2023",
];

export const trendCarouselItems: TrendCarouselItem[] = CAROUSEL_ORDER.flatMap(
  (slug) => {
    const entry = trendData[slug];
    if (!entry) return [];
    return [
      {
        id: entry.slug,
        title: entry.title,
        image: entry.heroImage,
        ...(NAVIGABLE_SLUGS.has(entry.slug)
          ? { url: `/trend/${entry.slug}` }
          : {}),
      },
    ];
  },
);
